/**
 * Builds the authoritative UIState projection from engine state.
 *
 * This file contains the sole function for deriving UIState from G and ctx.
 * The UI never reads G directly — it calls buildUIState to get a
 * JSON-serializable, engine-internal-free projection.
 *
 * No boardgame.io imports. No registry imports. No .reduce().
 * No mutation of G or ctx.
 *
 * WP-128 / EC-131 — extends the projection contract for the board-layout
 * wireframe (`docs/ai/DESIGN-BOARD-LAYOUT.md §4`). Eight projections
 * lack a `G` source today and ship as Option A safe-skips per D-12806;
 * each carries a `// SAFE-SKIP-WP128` marker and a 3-clause `// why:`
 * comment. CI grep enforces the marker count (≥ 8). Aliasing-defense
 * follows WP-111 D-11105 (per-entry shallow copy via resolveDisplay).
 */
import { computeMenace, menaceTierFor, resolveSchemeLossKind, resolveSchemeLossProgress, resolveSchemeLossThreshold, resolveTwistLossThreshold, } from '../rules/schemeLossProgress.js';
import { computeDayNight } from '../rules/dayNight.logic.js';
import { resolveRecruitCost } from '../hero/sizeChanging.logic.js';
// why: WP-313 — reuse the engine's authoritative victory-pile eligibility helper
// (victory pile ∩ villainDeckCardTypes ∈ {villain, henchman}, D-24608) so the projected list is
// byte-identical to what resolveVictoryPileCardPick re-filters at resolve time
// (the round-trip rule) — never a re-implemented filter.
import { getEligibleVictoryVillains } from '../moves/resolveVictoryPileCardPick.js';
import { isSplitFaceBindable, splitInstanceIntoBaseAndCopy } from '../moves/splitFaceChoice.resolve.js';
// why: D-24139 — reuse the engine's authoritative 0-cost discard eligibility helper so
// the projected list is byte-identical to what resolveReturnZeroCostDiscard validates
// at resolve time (the round-trip rule) — never a re-implemented filter.
import { getEligibleZeroCostDiscardCards } from '../moves/resolveReturnZeroCostDiscard.js';
// why: WP-383 / D-24184 — reuse the engine's authoritative discard-to-play eligibility
// helper (the chooser's whole hand) so the projected list is byte-identical to what
// resolveDiscardToPlay validates at resolve time (the round-trip rule).
import { getDiscardToPlayCost, getEligibleDiscardToPlayCards, hasPendingDiscardToPlay, } from '../moves/resolveDiscardToPlay.js';
import { getEligibleSmashDiscardCards } from '../moves/smashDiscard.resolve.js';
import { getEligiblePutHandOnDeckTopCards } from '../moves/putHandOnDeckTop.resolve.js';
import { DO_OVER_DRAW_COUNT } from '../moves/doOver.resolve.js';
import { getEligibleReturnOnDiscardCards } from '../moves/resolveReturnOnDiscard.js';
// why: WP-532 / D-24343 — reuse the engine's authoritative give-HQ-Hero eligibility helper
// so the projected list is byte-identical to what resolveGiveHqHeroChoice validates against
// (the round-trip rule) — never a re-implemented HQ filter.
import { getEligibleGiveHqHeroCards } from '../moves/giveHqHeroChoice.resolve.js';
// why: WP-535 / D-24345 — the copyable-Hero list is recomputed fresh here from the same
// predicate resolveCopyPowersChoice validates against, so the projected list is
// byte-identical to what the resolve move accepts (the round-trip rule).
import { getEligibleCopyPowersCards } from '../moves/copyPowersChoice.resolve.js';
// why: WP-783 / D-24629 — the phasable-hand-card list is the SAME predicate the phaseCard
// move validates against (one legality authority), so the button never offers a no-op.
import { phasingOptions } from '../moves/phaseCard.js';
import { ritualArtifactOptions } from '../moves/ritualArtifact.js';
import { getAvailableRecruit, getSpendableAttack, formatAttackTargets } from '../economy/economy.logic.js';
import { isRescueOnFightCueOn } from '../economy/rescueOnFight.logic.js';
import { resolveFightPrice, resolveMastermindFightPrice, darkPortalLocations, DARK_PORTAL_ATTACK_BONUS, } from '../economy/economy.resolve.js';
import { resolveCountSource } from '../hero/heroCountSource.resolve.js';
import { cardCountsAsShieldHero } from '../hero/effectiveTeams.logic.js';
import { evaluateEndgame } from '../endgame/endgame.evaluate.js';
import { computeFinalScores, isVictoryPileBystander } from '../scoring/scoring.logic.js';
import { WOUND_EXT_ID } from '../setup/buildInitialGameState.js';
import { isFinalBlowAvailable } from '../mastermind/mastermind.logic.js';
import { isMastermindHaunting } from '../board/haunt.logic.js';
import { SHIELD_OFFICER_EXT_ID, SIDEKICK_EXT_ID } from '../setup/pilesInit.js';
import { ENDGAME_CONDITIONS } from '../endgame/endgame.types.js';
import { buildKoEligibleTargets } from '../villain/villainEffects.execute.js';
import { getEligibleKoDiscardCards } from '../moves/koDiscardChoice.resolve.js';
// why: WP-817 / D-24750 — handCosts projects the same off-play cost the fight-discard
// gate reads (isHandCardCostOneOf), so the client and the engine agree on eligibility.
import { offPlayCardStats } from '../hero/splitCard.logic.js';
import { enigmaColorsOf } from '../rules/enigmaCode.logic.js';
// why: WP-111 / EC-118 / PS-8 — pure-render fallback for the rare case
// where a CardExtId in any zone has no matching entry in
// G.cardDisplayData. Centralized as a single named constant so the
// placeholder name literal appears EXACTLY ONCE across the engine
// source (grep-enforced — see EC-118 §After Completing). The
// diagnostic surface for missing entries lives at SETUP TIME (the
// auditCardDisplayDataCompleteness sweep in buildInitialGameState
// emits one consolidated message into G.messages mirroring WP-113
// D-10014). Projection-time use here is a pure render path — no G
// mutation. Cite WP-028 D-2801 (projection-purity contract) +
// pre-flight 2026-04-29 PS-8.
//
// Tests assert no placeholder appears for valid setups (CI-visible
// regression target).
/**
 * Pure-render placeholder for CardExtIds with no matching display entry.
 *
 * Exported so callers can perform structural equality checks against
 * the placeholder shape; the placeholder name literal appears nowhere
 * else in engine source. The `extId` field is intentionally an empty
 * string — at projection time, the actual ext_id is substituted in
 * via `{...UNKNOWN_DISPLAY_PLACEHOLDER, extId}` (see resolveDisplay).
 */
export const UNKNOWN_DISPLAY_PLACEHOLDER = {
    extId: '',
    name: '<unknown>',
    imageUrl: '',
    cost: null,
    heroClass: null,
    team: null,
};
// why: every projection-time read of G.cardDisplayData[extId] MUST
// return a fresh shallow copy at the projection boundary, not a direct
// reference. Standard tests cannot detect aliasing — line-by-line
// shallow copies are the contract. Mirrors the WP-028 cardKeywords
// post-mortem aliasing fix.
/**
 * Resolves the UICardDisplay for a CardExtId via shallow copy.
 *
 * Returns a fresh {...G.cardDisplayData[extId]} when the entry exists;
 * returns {...UNKNOWN_DISPLAY_PLACEHOLDER, extId} when missing (pure
 * render fallback — does not mutate G).
 */
function resolveDisplay(extId, gameState) {
    const entry = gameState.cardDisplayData[extId];
    // why: WP-179 — `heroClass` and `team` are always assigned on every
    // UICardDisplay (runtime shape guarantee despite optional TS typing).
    // `null` on lookup miss prevents `undefined` from leaking to the UI.
    const traitEntry = gameState.cardTraits !== undefined
        ? gameState.cardTraits[extId]
        : undefined;
    const heroClass = traitEntry !== undefined ? traitEntry.heroClass : null;
    const team = traitEntry !== undefined ? traitEntry.team : null;
    if (entry !== undefined) {
        return { ...entry, heroClass, team };
    }
    return { ...UNKNOWN_DISPLAY_PLACEHOLDER, extId, heroClass, team };
}
// why: WP-128 / D-12805 — produce a per-entry shallow-copied
// `UIDisplayEntry` array from a CardExtId list. Aliasing-defense per
// WP-111 D-11105: every entry is a fresh `{ extId, display }` object
// and the display payload is itself shallow-cloned via resolveDisplay.
// `[...zone]` would alias the entries; `zone.map(e => e)` would alias
// the shallow shape but not the extId/display pair — both are the
// forbidden patterns. The correct shape (this helper) builds fresh
// objects per entry.
/**
 * Builds a UIDisplayEntry[] from a CardExtId[] with per-entry shallow copy.
 */
function buildDisplayEntries(cardExtIds, gameState) {
    const entries = [];
    for (const extId of cardExtIds) {
        entries.push({ extId, display: resolveDisplay(extId, gameState) });
    }
    return entries;
}
// why: WP-789 / D-24637 §3 — a revealed deck-top card often stays in the deck,
// outside every projected zone, so the overlay could not resolve its name or
// image. Dedupe the `heroEffectResolved.revealedCardId`s in first-appearance order,
// then reuse buildDisplayEntries (as transformDeck does) for the per-entry copy.
/**
 * Builds the public display entries for every distinct revealed card named by a
 * `heroEffectResolved` notable event, in first-appearance order.
 */
function buildNotableEventCards(gameState) {
    const revealedCardIds = [];
    const seenCardIds = new Set();
    for (const notableEvent of gameState.notableEvents) {
        if (notableEvent.type !== 'heroEffectResolved') {
            continue;
        }
        const revealedCardId = notableEvent.revealedCardId;
        if (revealedCardId === undefined || seenCardIds.has(revealedCardId)) {
            continue;
        }
        seenCardIds.add(revealedCardId);
        revealedCardIds.push(revealedCardId);
    }
    return buildDisplayEntries(revealedCardIds, gameState);
}
// why: D-24020 — a SINGLE deterministic mapping from WP-248's reward
// (rewardType + magnitude) to the player-facing label rendered in the
// optional-KO-reward prompt. Defined ONCE here — never an ad-hoc or per-card
// string at any call site — so the label cannot drift as the future sweep WP
// marks more cards with this effect. The seeded rewardType set is
// rescue / draw / attack / recruit (D-24019, deferred to — not re-declared
// here). An unseeded value cannot occur (WP-248 filters at parse) but still
// returns a safe generic label rather than crashing the projection.
/**
 * Derives the reward label shown in the optional-KO-reward prompt.
 *
 * @param rewardType - WP-248's PendingOptionalKoReward.rewardType (a HeroKeyword
 *   string; the seeded set is rescue / draw / attack / recruit).
 * @param rewardMagnitude - the reward magnitude (≥1 for the seeded reward set).
 * @returns the single-mapping player-facing label string.
 */
function deriveOptionalKoRewardLabel(rewardType, rewardMagnitude) {
    // why: WP-667 / D-24480 — the NO-REWARD variant (Radioactive Riot) carries no reward,
    // so the label is empty; the client omits the reward clause and shows only "You may KO a
    // card from your hand or discard pile".
    if (rewardType === 'none') {
        return '';
    }
    if (rewardType === 'rescue') {
        return 'Rescue a Bystander';
    }
    if (rewardType === 'draw') {
        if (rewardMagnitude > 1) {
            return `Draw ${rewardMagnitude} cards`;
        }
        return 'Draw a card';
    }
    if (rewardType === 'attack') {
        return `+${rewardMagnitude} Attack`;
    }
    if (rewardType === 'recruit') {
        return `+${rewardMagnitude} Recruit`;
    }
    // why: WP-681 / D-24498 — Battlefield Promotion's reward gains a S.H.I.E.L.D. Officer
    // from supply to the acting player's HAND (auto-taken on KO, pure upside).
    if (rewardType === 'gain-officer-hand') {
        return 'Gain a S.H.I.E.L.D. Officer to your hand';
    }
    // why: D-24020 — an unseeded rewardType cannot occur (WP-248 filters at
    // parse); the generic fallback keeps the projection total rather than
    // returning undefined / crashing.
    return 'Take the reward';
}
/**
 * Derives the empowered-button label shown in the draw-or-empowered prompt.
 *
 * Single deterministic class→display mapping (WP-287 / D-24071) over WP-286's
 * PendingDrawOrEmpowered.empoweredClass — never an ad-hoc or per-card string.
 *
 * @param empoweredClass - WP-286's parsed hero-class slug (e.g. `strength`).
 * @returns the single-mapping player-facing label string.
 */
function deriveEmpoweredLabel(empoweredClass) {
    if (empoweredClass === 'strength') {
        return 'Empowered by Strength';
    }
    if (empoweredClass === 'instinct') {
        return 'Empowered by Instinct';
    }
    if (empoweredClass === 'covert') {
        return 'Empowered by Covert';
    }
    if (empoweredClass === 'tech') {
        return 'Empowered by Tech';
    }
    if (empoweredClass === 'ranged') {
        return 'Empowered by Ranged';
    }
    // why: D-24071 — an unrecognized class keeps the projection total (a safe
    // generic label) rather than returning undefined / crashing; the parser
    // should never emit one, but the projection must not depend on that.
    return 'Empowered';
}
/**
 * Parses a qualified scheme id `<setAbbr>/<slug>` into components.
 *
 * Returns null on malformed input. Used to build the cardDisplayData
 * lookup prefix for scheme display resolution.
 */
function parseSchemeIdForDisplay(schemeId) {
    if (typeof schemeId !== 'string' || schemeId.length === 0)
        return null;
    const slashIndex = schemeId.indexOf('/');
    if (slashIndex === -1)
        return null;
    const setAbbr = schemeId.slice(0, slashIndex);
    const slug = schemeId.slice(slashIndex + 1);
    if (setAbbr.length === 0 || slug.length === 0)
        return null;
    return { setAbbr, slug };
}
/**
 * Counts wound cards across all five player zones.
 *
 * // why: wounds are CardExtId strings mixed into player zones. There is
 * no dedicated wounds zone per player. Counting uses WOUND_EXT_ID constant
 * to identify wound cards across deck, hand, discard, inPlay, and victory.
 *
 * @param zones - The player's five card zones.
 * @returns The total number of wound cards in all zones.
 */
function countWounds(zones) {
    let woundCount = 0;
    // why: iterate each zone explicitly with for...of; no .reduce()
    for (const card of zones.deck) {
        if (card === WOUND_EXT_ID) {
            woundCount += 1;
        }
    }
    for (const card of zones.hand) {
        if (card === WOUND_EXT_ID) {
            woundCount += 1;
        }
    }
    for (const card of zones.discard) {
        if (card === WOUND_EXT_ID) {
            woundCount += 1;
        }
    }
    for (const card of zones.inPlay) {
        if (card === WOUND_EXT_ID) {
            woundCount += 1;
        }
    }
    for (const card of zones.victory) {
        if (card === WOUND_EXT_ID) {
            woundCount += 1;
        }
    }
    return woundCount;
}
// why: aggregation happens at projection time instead of tracking a first-class
// counter. If write-path events need a counter later, introduce
// ENDGAME_CONDITIONS.BYSTANDERS_RESCUED in a separate WP.
/**
 * Counts bystanders across every player's victory zone.
 *
 * Iterates only the `victory` zone of each player. Bystanders in hand, deck,
 * discard, or inPlay are deliberately excluded — a bystander outside victory
 * is not yet rescued.
 *
 * @param gameState - The engine state. Not mutated.
 * @returns Total count of bystanders sitting in any player's victory zone.
 */
function countBystandersRescued(gameState) {
    let bystanderCount = 0;
    // why: iterate every player's victory zone explicitly with for...of; no
    // .reduce() with branching per code-style Rule 8.
    //
    // why: bystanders in victory come from two sources — villain-deck
    // bystanders (tracked in G.villainDeckCardTypes with type='bystander')
    // and rescued / awarded supply-pile bystanders (BYSTANDER_EXT_ID, NOT
    // registered in villainDeckCardTypes), plus defeated Killbots (rules v23
    // L2341). isVictoryPileBystander (scoring.logic.ts) is the single source of
    // truth for that condition (WP-586), so the HUD counter, the VP calculation,
    // and the competitive-score inputs can never disagree on what a "bystander
    // in the victory pile" is.
    for (const playerZones of Object.values(gameState.playerZones)) {
        for (const cardExtId of playerZones.victory) {
            if (isVictoryPileBystander(gameState, cardExtId)) {
                bystanderCount += 1;
            }
        }
    }
    return bystanderCount;
}
/**
 * Builds the UIProgressCounters projection for the HUD.
 *
 * Always returns a fully populated counters object — both fields are required
 * on every UIState even during the lobby phase, where both values are zero.
 *
 * @param gameState - The engine state. Not mutated.
 * @returns Aggregate progress counters projection.
 */
function buildProgressCounters(gameState) {
    // why: counter is lazily initialised on first escape; absence is
    // semantically zero.
    const escapedVillains = gameState.counters[ENDGAME_CONDITIONS.ESCAPED_VILLAINS] ?? 0;
    // why: WP-557 / D-24366 §2 — the menace signal is DERIVED here at projection
    // time and never stored in G, so it adds no state-hash surface and neither
    // sentinel oracle re-pins. The derivation itself lives in
    // rules/schemeLossProgress.ts, shared with the twist dispatcher; re-deriving
    // the threshold locally would create the second copy this packet removed.
    const menace = computeMenace(gameState);
    const schemeLossThreshold = resolveSchemeLossThreshold(gameState);
    const progressCounters = {
        bystandersRescued: countBystandersRescued(gameState),
        escapedVillains,
        menace,
        menaceTier: menaceTierFor(menace),
        schemeLossProgress: resolveSchemeLossProgress(gameState),
        // why: WP-562 / D-24371 §3 — the kind is what lets the client write
        // "Heroes 11/42" without re-deriving the scheme's loss condition. It is
        // resolved here from the same module as the numerator, so the label and
        // the numbers can never describe different quantities.
        schemeLossKind: resolveSchemeLossKind(gameState),
        // why: WP-562 / D-24371 §5 — the twist readout's own denominator. Distinct
        // from schemeLossThreshold above, which for a resourceLossCondition scheme
        // counts a resource rather than twists.
        schemeTwistThreshold: resolveTwistLossThreshold(gameState),
    };
    // why: omit the key entirely rather than emitting `undefined`. An absent key
    // and an explicit `undefined` read differently under
    // `exactOptionalPropertyTypes`, and "this state carries no denominator" is
    // the assertion being made. Post-WP-562 every scheme resolves one; only a
    // pre-WP-562 recorded pile-depleted state reaches this branch.
    if (schemeLossThreshold !== undefined) {
        progressCounters.schemeLossThreshold = schemeLossThreshold;
    }
    return progressCounters;
}
/**
 * Projects G.villainAttachedHeroes into a fresh Record with spread-copied
 * inner arrays to prevent aliasing with G state.
 *
 * @param villainAttachedHeroes - Source from G (may be undefined in old test states).
 * @returns A new Record with spread-copied hero id arrays.
 */
// why: WP-214 — per-entry spread ensures inner arrays are also new references,
// so UIState consumers cannot mutate G.villainAttachedHeroes through the projection
function buildVillainAttachedHeroesProjection(villainAttachedHeroes) {
    if (!villainAttachedHeroes) {
        return {};
    }
    const result = {};
    for (const villainId of Object.keys(villainAttachedHeroes)) {
        const heroes = villainAttachedHeroes[villainId];
        if (heroes !== undefined) {
            result[villainId] = [...heroes];
        }
    }
    return result;
}
// why: per D-6701, PAR payload is deferred until `buildUIState` has access to
// a `ReplayResult`. The type-level contract ships via `UIParBreakdown` and the
// drift test locks the four field names. Body stays `return undefined;`
// unconditionally — no call to `deriveScoringInputs` / `buildScoreBreakdown`.
// A follow-up WP resolves the data source.
/**
 * Builds the optional UIParBreakdown projection for the endgame HUD.
 *
 * Per D-6701 the body is `return undefined;` unconditionally at MVP; the
 * type-level contract ships via `UIParBreakdown` and the drift test pins the
 * four field names. The follow-up WP that supplies the payload modifies only
 * this body — `buildUIState` already preserves the wire via conditional spread.
 *
 * @param gameState - The engine state. Not used in the safe-skip body.
 * @param ctx - The build context. Not used in the safe-skip body.
 * @returns Always undefined under D-6701; payload is deferred.
 */
function buildParBreakdown(gameState, ctx) {
    // why: D-6701 safe-skip — explicit void references keep the parameters in
    // the signature for the follow-up WP without tripping `noUnusedParameters`
    // when it is later enabled, and prove this body has no other intent.
    void gameState;
    void ctx;
    return undefined;
}
/**
 * Derives the authoritative UIState from engine state.
 *
 * Pure function: no I/O, no mutation of G or ctx, no side effects.
 * Same G + ctx always produces the same UIState.
 *
 * Forbidden behaviors (do not add later):
 * - caching or memoization
 * - closures over G or ctx
 * - mutation via object aliasing
 * - any form of side effect or state retention between calls
 *
 * // why: UIState is the only state the UI consumes. This function
 * implements D-0301 (UI Consumes Projections Only). All items in the
 * canonical forbidden internals list are excluded.
 *
 * @param gameState - The current engine state (G). Not mutated.
 * @param ctx - Minimal context with phase, turn, currentPlayer.
 * @returns The derived UIState projection.
 */
/**
 * Collects the deduped, insertion-ordered set of non-empty card-face image URLs
 * across every entry in G.cardDisplayData — the complete set of images this match
 * can show.
 *
 * why: WP-410 / D-24222 — the arena client warms these into the browser image
 * cache at match start so a card paints from cache on reveal instead of
 * round-tripping to R2 mid-turn. The client cannot derive the set itself (it may
 * not import the registry at runtime, and the per-zone projection only carries
 * currently-visible cards), so the engine — which already resolved every card's
 * imageUrl into cardDisplayData at setup — projects the deduped list. Empty
 * imageUrls (a card without art) are skipped; duplicates (heroes share card
 * designs) collapse to a single warm. Deterministic: object-value order follows
 * cardDisplayData's own deterministic insertion order.
 *
 * @param cardDisplayData - G.cardDisplayData: every match card's display payload.
 * @returns Deduped, non-empty image URL strings in first-seen order.
 */
function buildMatchCardImageManifest(cardDisplayData) {
    const seenImageUrls = new Set();
    const manifest = [];
    for (const display of Object.values(cardDisplayData)) {
        const imageUrl = display.imageUrl;
        if (imageUrl !== '' && !seenImageUrls.has(imageUrl)) {
            seenImageUrls.add(imageUrl);
            manifest.push(imageUrl);
        }
    }
    return manifest;
}
/**
 * Whether the match has any day/night hero hook (WP-765 / D-24598): a hook carrying a
 * sunlightInEffect / moonlightInEffect condition, or the fused day-night-both keyword.
 *
 * // why: the presence rule for UIHQState.dayNight — the badge is only meaningful when a
 * Sunlight/Moonlight card is in the match. The day-night-both keyword is checked too because a
 * Warlock-only match's fused hooks carry no day/night condition of their own.
 *
 * @param gameState - Current game state (read-only).
 * @returns Whether any hook is a day/night hook.
 */
function matchHasDayNightHook(gameState) {
    for (const hook of gameState.heroAbilityHooks ?? []) {
        for (const keyword of hook.keywords) {
            if (keyword === 'day-night-both') {
                return true;
            }
        }
        for (const condition of hook.conditions ?? []) {
            if (condition.type === 'sunlightInEffect' || condition.type === 'moonlightInEffect') {
                return true;
            }
        }
    }
    return false;
}
/**
 * Builds the HQ projection: the verbatim slots, the parallel slotDisplay, and — only for a
 * match with a day/night hero hook — the current Sunlight / Moonlight state (WP-765 / D-24598).
 *
 * @param hqSlots - The HQ slot ext_ids (null for an empty slot).
 * @param hqSlotDisplay - The slot-aligned display payloads.
 * @param gameState - Current game state (read-only).
 * @returns The UIHQState, with dayNight omitted when the match has no day/night hook.
 */
function buildHqProjection(hqSlots, hqSlotDisplay, gameState, activePlayerId) {
    const hq = { slots: hqSlots, slotDisplay: hqSlotDisplay };
    if (matchHasDayNightHook(gameState)) {
        hq.dayNight = computeDayNight(gameState);
    }
    if (matchHasSizeChangingCard(gameState)) {
        hq.recruitCosts = buildHqRecruitCosts(hqSlots, gameState, activePlayerId);
    }
    return hq;
}
/**
 * Whether any card in the match prints Size-Changing or Microscopic Size-Changing (D-24801).
 *
 * // why: the presence rule for UIHQState.recruitCosts — only a Size-Changing card can make a
 * slot's price differ from its printed cost.
 *
 * @param gameState - Current game state (read-only).
 * @returns Whether either Size-Changing map has an entry.
 */
function matchHasSizeChangingCard(gameState) {
    const sizeChangingCount = Object.keys(gameState.cardSizeChangingClasses ?? {}).length;
    const microscopicCount = Object.keys(gameState.cardMicroscopicSizeChangingClasses ?? {}).length;
    return sizeChangingCount > 0 || microscopicCount > 0;
}
/**
 * Builds the slot-aligned recruit prices for the active player (D-24801).
 *
 * @param hqSlots - The HQ slot ext_ids (null for an empty slot).
 * @param gameState - Current game state (read-only).
 * @param activePlayerId - The player whose played cards earn the discounts.
 * @returns One price per slot (null for an empty slot); negative only through Microscopic.
 */
function buildHqRecruitCosts(hqSlots, gameState, activePlayerId) {
    const recruitCosts = [];
    for (const slotCardId of hqSlots) {
        if (slotCardId === null) {
            recruitCosts.push(null);
        }
        else {
            recruitCosts.push(resolveRecruitCost(gameState, activePlayerId, slotCardId));
        }
    }
    return recruitCosts;
}
export function buildUIState(gameState, ctx) {
    // --- 1. Project game phase/turn/active player from ctx ---
    // why: game metadata comes from ctx (framework) and G.currentStage (engine)
    const game = {
        phase: ctx.phase ?? 'unknown',
        // why: WP-331 — the HUD turn header must read the same source the game log
        // numbers its lines by (G.logMeta.turn, the PLAY-RELATIVE turn stamped in the
        // play-phase onBegin), NOT live ctx.turn. logMeta.turn is offset so the first
        // play turn reads 1 (the lobby phase consumes framework turn 1, so ctx.turn is
        // one ahead throughout play); reading it here keeps the header and the log in
        // lock-step. It also fixes the game-over display: when the match ends,
        // boardgame.io transitions play -> end and a phase change bumps ctx.turn one
        // further past the last play turn, but the end phase has no onBegin, so
        // logMeta.turn stays frozen at the last play-relative turn and matches the log.
        // Fall back to ctx.turn before the first play-phase onBegin has stamped logMeta
        // (lobby/setup), where the framework turn is the only number available.
        turn: gameState.logMeta?.turn ?? ctx.turn,
        activePlayerId: ctx.currentPlayer,
        currentStage: gameState.currentStage,
        // why: WP-380 — coerce the optional WP-379 per-turn G flags to definite
        // boolean projections (undefined = not-yet-acted / not-yet-healed). These
        // drive the client Heal-Wounds affordance gating (D-24181).
        hasActedThisTurn: gameState.hasActedThisTurn ?? false,
        hasHealedThisTurn: gameState.hasHealedThisTurn ?? false,
        // why: coerce the optional per-turn reveal flag to a definite boolean
        // projection (undefined = reveal not yet spent = false). Gates the client's
        // start-stage flow (reveal-disabled-once-spent; Pass-priority-blocked-until-
        // revealed) to mirror the engine advanceStage reveal-first guard.
        villainRevealedThisTurn: gameState.villainRevealedThisTurn ?? false,
        // why: WP-409 — coerce the optional per-turn hero-effect-fired count to a
        // definite projection (undefined = no play yet this turn = 0).
        lastPlayEffectsFired: gameState.lastPlayEffectsFired ?? 0,
    };
    // --- 2. Project player states ---
    // why: zone counts hide card identities from the UI; wound count
    // uses WOUND_EXT_ID constant to identify wound cards across all zones
    //
    // why: WP-128 / D-12801 — `victoryVP` is projected by the engine via
    // `computeFinalScores` (single source of truth per WP-020). Indexed by
    // playerId so the per-player loop below doesn't depend on `players`
    // ordering. computeFinalScores is pure and cheap; one call per
    // projection.
    const finalScores = computeFinalScores(gameState);
    const totalVPByPlayer = {};
    for (const breakdown of finalScores.players) {
        totalVPByPlayer[breakdown.playerId] = breakdown.totalVP;
    }
    const players = [];
    for (const playerId of Object.keys(gameState.playerZones)) {
        const zones = gameState.playerZones[playerId];
        // why: handDisplay length-equals-handCards invariant — populate one
        // UICardDisplay per hand card via per-entry shallow copy
        // (resolveDisplay). filterUIStateForAudience redacts handDisplay
        // alongside handCards (privacy symmetry — see uiState.filter.ts).
        const handDisplay = [];
        // why: WP-817 / D-24750 — handCosts is index-aligned with handCards; null for a
        // card with no cost entry (a Wound). Redacted alongside handCards by the filter.
        const handCosts = [];
        for (const cardExtId of zones.hand) {
            handDisplay.push(resolveDisplay(cardExtId, gameState));
            handCosts.push(offPlayCardStats(gameState, cardExtId)?.cost ?? null);
        }
        // why: WP-128 / D-12803 — inPlayDisplay length-equals-inPlayCards
        // invariant. Audience filter redacts both fields together for
        // non-self / spectator audiences (mirrors handCards posture).
        const inPlayDisplay = [];
        for (const cardExtId of zones.inPlay) {
            inPlayDisplay.push(resolveDisplay(cardExtId, gameState));
        }
        // why: WP-128 / D-12803 — discard top is the last entry in the
        // discard array; `null` when discard is empty (count === 0). Optional
        // (`discardTopCard?`) encodes "redacted by audience filter"; `null`
        // encodes "visible but empty" — the pair distinguishes the two
        // states unambiguously.
        const discardLength = zones.discard.length;
        let discardTopCard;
        if (discardLength === 0) {
            discardTopCard = null;
        }
        else {
            const topExtId = zones.discard[discardLength - 1];
            discardTopCard = {
                extId: topExtId,
                display: resolveDisplay(topExtId, gameState),
            };
        }
        // why: WP-243 / D-24010 — full own-discard projection. discardCards is the
        // verbatim discard ext_ids (spread copy prevents aliasing with
        // G.playerZones[playerId].discard); discardDisplay is the parallel
        // per-card resolveDisplay result (one entry per discard card, fresh shallow
        // copy). Built in the SAME block (both or neither, length-matched).
        // filterUIStateForAudience redacts both together for non-owner / spectator
        // (the handCards privacy posture). Makes the buried discard targets
        // renderable for the KO-a-Hero prompt and the "View all" discard view.
        const discardCards = [...zones.discard];
        const discardDisplay = [];
        for (const cardExtId of zones.discard) {
            discardDisplay.push(resolveDisplay(cardExtId, gameState));
        }
        // why: WP-128 / D-12803 — VP cards are public knowledge by design;
        // not redacted by audience filter. WP-111 D-11105 aliasing-defense:
        // every entry is a fresh `{ extId, display }` with display itself
        // shallow-cloned (via resolveDisplay).
        const victoryCards = buildDisplayEntries(zones.victory, gameState);
        // why: WP-128 / D-12801 — uppercase `VP` matches canonical
        // `PlayerScoreBreakdown.totalVP` engine convention (`00.6` Rule 14).
        const victoryVP = totalVPByPlayer[playerId] ?? 0;
        // why: WP-608 / D-24419 — own draw-pool card stats: a SEPARATE extId ->
        // { recruit, attack, cost } map from the internal G.cardStats (not enriching
        // deckComposition; not on the 7-field-locked UICardDisplay — recruit/attack
        // are gameplay values). Keys are the SORTED unique ext_ids of deck ∪ discard:
        // a Record serializes in insertion order, so raw-array order would leak the
        // next-draw sequence even to the owner; the sort strips it (the
        // deckComposition rationale). Copy the three fields (not the CardStatEntry)
        // so the projection never aliases G.cardStats. Non-hero cards (no cardStats
        // entry, e.g. Wounds) are omitted -> the client defaults 0/0. Owner-only:
        // filterUIStateForAudience redacts it (the keys reveal the pool).
        // Projection-only: no G write, no state-hash surface.
        const sortedPoolCards = [...new Set([...zones.deck, ...zones.discard])].sort();
        const deckCardStats = {};
        for (const poolCardExtId of sortedPoolCards) {
            const cardStat = gameState.cardStats[poolCardExtId];
            if (cardStat !== undefined) {
                deckCardStats[poolCardExtId] = {
                    recruit: cardStat.recruit,
                    attack: cardStat.attack,
                    cost: cardStat.cost,
                };
            }
        }
        const playerState = {
            playerId,
            deckCount: zones.deck.length,
            handCount: zones.hand.length,
            discardCount: zones.discard.length,
            inPlayCount: zones.inPlay.length,
            victoryCount: zones.victory.length,
            woundCount: countWounds(zones),
            // why: hand card ext_ids included so filterUIStateForAudience can
            // expose them to the owning player. Spread copy prevents aliasing
            // with G.playerZones[playerId].hand.
            handCards: [...zones.hand],
            handDisplay,
            handCosts,
            // why: WP-128 / D-12803 — in-play card ext_ids included; filter
            // redacts for non-self / spectator. Spread copy prevents aliasing
            // with G.playerZones[playerId].inPlay.
            inPlayCards: [...zones.inPlay],
            inPlayDisplay,
            discardTopCard,
            // why: WP-243 / D-24010 — own-player full discard contents; filter
            // redacts both fields together for non-self / spectator. Assigned in
            // the same object literal so they are always present together at build
            // time (length-matched).
            discardCards,
            discardDisplay,
            // why: WP-606 / D-24417 — own draw-pool composition: the order-stripped
            // (sorted) multiset of this player's deck. `[...zones.deck]` copies before
            // sorting so G is never mutated; the sort strips draw ORDER so the
            // next-draw sequence never leaks (composition, not sequence — the WP-470
            // scry-KO secret). filterUIStateForAudience redacts it for non-self /
            // spectator (the discardCards posture). Projection-only: derived here,
            // never stored on G, no state-hash surface.
            deckComposition: [...zones.deck].sort(),
            deckCardStats,
            victoryCards,
            victoryVP,
        };
        // why: WP-803 / D-24692 — the cards this player set aside Out of Time (public; the
        // `victoryCards` disposition). Omitted when empty so a match without an Out of Time card
        // projects exactly as before.
        const outOfTimeCardIds = gameState.outOfTimeCards?.[playerId] ?? [];
        if (outOfTimeCardIds.length > 0) {
            playerState.outOfTimeCards = buildDisplayEntries(outOfTimeCardIds, gameState);
        }
        // why: WP-858 / D-24794 — the Ritual Artifacts this player controls (public; the
        // `outOfTimeCards` disposition). Omitted when empty so a match without one projects exactly
        // as before.
        const controlledArtifactIds = gameState.controlledArtifacts?.[playerId] ?? [];
        if (controlledArtifactIds.length > 0) {
            playerState.controlledArtifacts = buildDisplayEntries(controlledArtifactIds, gameState);
        }
        // why: WP-848 / D-24784 — the live WP-708 conditional-clause tally. Reads the
        // hash-excluded G.diagnostics channel (D-24271), so no hash re-pins. A fresh
        // { played, assembled } copy so the projection never aliases G.diagnostics; omitted
        // when the seat has no entry (an entry with played 0 is still projected).
        const conditionalClauseEntry = gameState.diagnostics?.conditionalClauses?.[playerId];
        if (conditionalClauseEntry !== undefined) {
            playerState.conditionalClauses = {
                played: conditionalClauseEntry.played,
                assembled: conditionalClauseEntry.assembled,
            };
        }
        players.push(playerState);
    }
    // --- 3. Project City ---
    // why: city projection includes type and keywords for display without
    // exposing the raw villainDeckCardTypes or cardKeywords maps
    const citySpaces = [];
    for (const space of gameState.city) {
        if (space === null) {
            citySpaces.push(null);
        }
        else {
            // why: spread operator creates a new array to prevent aliasing
            // with G.cardKeywords — UIState must not hold references to G data
            const cardKeywords = gameState.cardKeywords[space];
            // why: WP-214 — spread copy prevents aliasing with G.villainAttachedHeroes
            const spaceAttachedHeroes = gameState.villainAttachedHeroes?.[space] ?? [];
            // why: WP-505 — count only of face-down captured bystanders. Bystanders
            // captured under a city villain are FACE DOWN, so their identity stays
            // hidden — project the count (rendered as an "N captured" badge), never
            // the ext_ids. This reverses the D-12806 city-villain safe-skip for the
            // count only (D-24311); it is still never flattened onto the mastermind.
            const spaceAttachedBystanders = gameState.attachedBystanders?.[space] ?? [];
            const spaceFightDiscardCosts = gameState.villainFightDiscardCosts?.[space];
            citySpaces.push({
                extId: space,
                type: gameState.villainDeckCardTypes[space] ?? 'unknown',
                keywords: cardKeywords !== undefined ? [...cardKeywords] : [],
                // why: WP-111 — additive display projection; per-entry shallow
                // copy via resolveDisplay prevents aliasing with
                // G.cardDisplayData[space].
                display: resolveDisplay(space, gameState),
                // why: WP-214 — attached heroes projected as string[] for arena-client
                // rendering; spread copy ensures no aliasing with G state
                attachedHeroes: [...spaceAttachedHeroes],
                // why: WP-505 — face-up captured heroes need a display payload to
                // render as art (the client has no ext_id→image resolver); resolveDisplay
                // per hero, index-aligned with attachedHeroes above.
                attachedHeroDisplay: spaceAttachedHeroes.map((heroId) => resolveDisplay(heroId, gameState)),
                // why: WP-505 / D-24311 — count only (face-down = identity hidden).
                attachedBystanderCount: spaceAttachedBystanders.length,
                // why: WP-214 — engine-resolved fight cost; UI must not recompute
                // dynamic values (engine-owns-truth invariant). WP-760 / D-24589: passes the
                // active player — the only player who can fight — so a Blood Frenzy villain
                // shows that player's cost to every audience.
                // D-24802: the Size-Changing price for the active player, never below 0 (the debit
                // fightVillain makes before Patrol), so the tile gates on what the fight costs.
                fightCost: Math.max(0, resolveFightPrice(gameState, space, ctx.currentPlayer)),
                // why: WP-817 / D-24750 — the fight discard cost is public printed card text;
                // a fresh copy, present only for a villain that has one (Primus).
                ...(spaceFightDiscardCosts !== undefined
                    ? { fightDiscardCosts: [...spaceFightDiscardCosts.eligibleCosts] }
                    : {}),
            });
        }
    }
    const escapedPile = buildDisplayEntries(gameState.escapedPile, gameState);
    // --- 4. Project HQ ---
    // why: HQ slots expose ext_ids for registry display lookup; no
    // engine internals needed. UIHQState.slots shape is preserved
    // verbatim per pre-flight 2026-04-29 PS-6 (Q3 audit).
    const hqSlots = [];
    // why: WP-111 — slotDisplay parallel array; length-equals-slots
    // invariant; null at index i must match slots[i] === null. Per-entry
    // shallow copy via resolveDisplay prevents aliasing.
    const hqSlotDisplay = [];
    for (const slot of gameState.hq) {
        hqSlots.push(slot);
        if (slot === null) {
            hqSlotDisplay.push(null);
        }
        else {
            hqSlotDisplay.push({
                extId: slot,
                display: resolveDisplay(slot, gameState),
            });
        }
    }
    // why: WP-757 / D-24587 — project the per-slot haunters, index-aligned with the HQ.
    // A Villain haunter embeds its display (the client has no extId → display resolver).
    // Undefined when G.hqHaunters is absent, so the field is omitted (hash-neutral).
    let hqHaunters;
    if (gameState.hqHaunters !== undefined) {
        hqHaunters = [];
        for (const haunter of gameState.hqHaunters) {
            if (haunter === null || haunter === undefined) {
                hqHaunters.push(null);
            }
            else if (haunter.kind === 'villain') {
                hqHaunters.push({
                    kind: 'villain',
                    extId: haunter.cardId,
                    display: resolveDisplay(haunter.cardId, gameState),
                });
            }
            else {
                hqHaunters.push({ kind: 'mastermind' });
            }
        }
    }
    // --- 5. Project mastermind ---
    // why: tactics projected as counts, not card arrays. display lookup
    // uses gameState.mastermind.baseCardId (the canonical G.cardStats /
    // G.cardDisplayData join key per mastermind.setup.ts:211), NOT
    // gameState.mastermind.id (the qualified group id). Per-entry shallow
    // copy via resolveDisplay prevents aliasing. Cite pre-flight
    // 2026-04-29 PS-5.
    //
    // why: WP-128 / D-12805 — `G.mastermind.attachedBystanders` IS populated at
    // runtime (D-24654 capture sources: a Villain-Deck Bystander revealed with an
    // empty City, Here, Hold This, the kidnap fallback); project it directly. **Still do NOT flatten
    // `G.attachedBystanders`** (the top-level city-villain captures) into this
    // field — D-12805 Interpretation B keeps the two capture stores separate;
    // city-villain bystanders render on the city row as
    // `UICityCard.attachedBystanderCount` (WP-505 / D-24311), never here.
    //
    const mastermindAttachedBystanders = buildDisplayEntries(gameState.mastermind.attachedBystanders, gameState);
    const mastermindStrikePile = buildDisplayEntries(gameState.mastermind.strikePile, gameState);
    // why: WP-399 / D-24202 — project the D-24201 Hypno-Thrall zone through the
    // SAME buildDisplayEntries path as attachedBystanders / strikePile above, so
    // an id missing from cardDisplayData degrades identically (no parallel
    // resolver, no `<unknown>` placeholder). `?? []` because hypnoThralls is
    // optional on G (the gameText precedent); an absent zone projects `[]`.
    const mastermindHypnoThralls = buildDisplayEntries(gameState.mastermind.hypnoThralls ?? [], gameState);
    const mastermind = {
        id: gameState.mastermind.id,
        tacticsRemaining: gameState.mastermind.tacticsDeck.length,
        tacticsDefeated: gameState.mastermind.tacticsDefeated.length,
        display: resolveDisplay(gameState.mastermind.baseCardId, gameState),
        attachedBystanders: mastermindAttachedBystanders,
        strikePile: mastermindStrikePile,
        hypnoThralls: mastermindHypnoThralls,
        gameText: gameState.mastermind.gameText ?? [],
        // why: WP-687 / D-24504 — the Final Blow "fight the Mastermind again" affordance.
        // Single source of truth with the fightMastermind final-fight gate: both read
        // isFinalBlowAvailable, so the tile can never show "fight again" when the engine
        // would refuse it. Always a boolean here; the audience filter must pass it
        // through (the EC-206 drop) — see uiState.filter.ts.
        finalBlowPending: isFinalBlowAvailable(gameState.mastermind, gameState.finalBlow),
        // why: WP-750 / D-24574 — the projected fight cost, from the same
        // resolveMastermindFightCost the fightMastermind guard and the bot read, so the
        // tile's Fight gate can never disagree with the engine (printed + Dark Portal).
        // WP-818 / D-24752: read for the active player (the only one who can fight), so
        // Baron Zemo's "+9 unless you are a Savior" shows that player's cost to everyone.
        // D-24802: the Size-Changing price (Fin Fang Foom), the same read fightMastermind gates on.
        fightCost: resolveMastermindFightPrice(gameState, ctx.currentPlayer),
        // why: WP-757 / D-24587 — omit-when-absent: the key exists only while the Mastermind
        // haunts, read from the same isMastermindHaunting predicate fightMastermind uses.
        ...(isMastermindHaunting(gameState) ? { isHaunting: true } : {}),
    };
    // --- 6. Project scheme — derive twist count ---
    // why: scheme-twist cards route to G.scheme.twistPile (not
    // villainDeck.discard) per villainDeck.reveal.ts — count the pile
    // directly so the UI reflects actual resolved twists.
    const twistCount = gameState.scheme.twistPile.length;
    const schemeTwistPile = buildDisplayEntries(gameState.scheme.twistPile, gameState);
    // why: scheme display resolved from cardDisplayData using the flat card
    // key format `{setAbbr}-scheme-{slug}`. The scheme ext_id in selection
    // is the qualified group id (e.g. 'core/midtown-bank-robbery'); the
    // flat card key replaces the slash with `-scheme-`.
    const schemeParsed = parseSchemeIdForDisplay(gameState.selection.schemeId);
    let schemeDisplay = {
        extId: gameState.selection.schemeId,
        name: gameState.selection.schemeId.replace(/-/g, ' '),
        imageUrl: '',
        cost: null,
    };
    if (schemeParsed !== null) {
        const schemeFlatKey = `${schemeParsed.setAbbr}-scheme-${schemeParsed.slug}`;
        if (gameState.cardDisplayData[schemeFlatKey] !== undefined) {
            schemeDisplay = resolveDisplay(schemeFlatKey, gameState);
        }
    }
    // why: WP-728 / D-24549 — project the Portals Dark-Portal locations + their +N
    // attack buffs for the board overlay (WP-727). darkPortalLocations is the single
    // source (same predicate combat reads); the descriptor is omitted for every
    // non-Portals scheme (the gameText? / finalBlowPending? conditional-spread
    // precedent), so `hasDarkPortals` is false whenever no portal has opened.
    const portalLocations = darkPortalLocations(gameState);
    const hasDarkPortals = portalLocations.onMastermind || portalLocations.citySpaceIndices.length > 0;
    const enigmaCode = buildEnigmaCodeProjection(gameState);
    const scheme = {
        id: gameState.selection.schemeId,
        twistCount,
        twistPile: schemeTwistPile,
        display: schemeDisplay,
        gameText: gameState.scheme.gameText ?? [],
        ...(hasDarkPortals
            ? {
                darkPortals: {
                    onMastermind: portalLocations.onMastermind,
                    mastermindAttackBonus: portalLocations.onMastermind
                        ? DARK_PORTAL_ATTACK_BONUS
                        : 0,
                    citySpaceIndices: portalLocations.citySpaceIndices,
                    citySpaceAttackBonus: DARK_PORTAL_ATTACK_BONUS,
                },
            }
            : {}),
        // why: WP-830 / D-24763 — the full code (every card's display + colors); the audience filter
        // strips what a viewer has not seen. Omitted while the code is empty or absent.
        ...(enigmaCode !== undefined ? { enigmaCode } : {}),
    };
    // --- 7. Project economy ---
    // why: available amounts computed via engine helpers, not raw
    // subtraction, to stay consistent with move validation logic
    //
    const piercing = gameState.turnEconomy.piercing;
    const woundsDrawn = gameState.turnEconomy.woundsDrawn;
    const currentPhasingOptions = phasingOptions(gameState, ctx.currentPlayer);
    const currentRitualArtifactOptions = ritualArtifactOptions(gameState, ctx.currentPlayer);
    // why: WP-790 / D-24652 — project the restricted-attack grants that still have attack left
    // (Board-Visible Field Rule step 2), with copied `targets` so the projection never aliases G,
    // and the display label the economy bar shows. availableAttack (getSpendableAttack) excludes
    // these amounts, so the client needs them to enable a Fight button the engine would accept.
    const restrictedAttack = [];
    for (const grant of gameState.turnEconomy.restrictedAttack ?? []) {
        if (grant.remaining > 0) {
            restrictedAttack.push({
                remaining: grant.remaining,
                targets: [...grant.targets],
                label: formatAttackTargets(grant.targets),
            });
        }
    }
    const economy = {
        attack: gameState.turnEconomy.attack,
        recruit: gameState.turnEconomy.recruit,
        // why: WP-580 / D-24389 — availableAttack reflects the combined spendable
        // figure (attack + unspent recruit) while the recruit-as-attack conversion
        // is active this turn, so the play surface shows what a fight can actually be
        // funded with. Equal to getAvailableAttack on every non-conversion turn.
        availableAttack: getSpendableAttack(gameState.turnEconomy),
        availableRecruit: getAvailableRecruit(gameState.turnEconomy),
        piercing,
        woundsDrawn,
        // why: WP-581 / D-24390 — project the recruit-as-attack conversion flag
        // omit-when-absent (present only when set), so a non-conversion turn's
        // economy block is byte-identical to pre-WP-581 and the client can render a
        // cue only while God of Thunder's conversion is active this turn.
        ...(gameState.turnEconomy.recruitSpendableAsAttack === true
            ? { recruitSpendableAsAttack: true }
            : {}),
        // why: WP-739 / D-24560 — project the Excessive Violence availability cue
        // omit-when-absent (present only when true), so the WP-738 client can offer
        // "Fight using Excessive Violence" only when it would do something. True iff
        // the player enrolled >= 1 EV card this turn AND has not yet used EV. Surfaces
        // WHETHER EV is available, never the ledger contents (no CardExtId leaked).
        ...((gameState.turnEconomy.excessiveViolencePlayedCards?.length ?? 0) > 0 &&
            gameState.turnEconomy.excessiveViolenceUsedThisTurn !== true
            ? { excessiveViolenceAvailable: true }
            : {}),
        // why: WP-819 / D-24753 — project Baron Zemo's rescue-on-fight availability cue
        // active-player-only and omit-when-absent (present only when true), so every match
        // without Zemo keeps the economy block byte-identical and the WP-820 client offers
        // "Fight + Rescue" only when it could do something.
        ...(isRescueOnFightCueOn(gameState) ? { rescueOnFightAvailable: true } : {}),
        // why: WP-842 / D-24776 — project the once-per-turn Sidekick recruit cue omit-when-absent
        // (present only when set), so a turn without a Sidekick recruit keeps the economy block
        // byte-identical. The filter passes it to the active player only.
        ...(gameState.turnEconomy.hasRecruitedSidekickThisTurn === true
            ? { hasRecruitedSidekickThisTurn: true }
            : {}),
        // why: WP-783 / D-24629 — project the Phasing options omit-when-absent (present only
        // when at least one hand card is phasable), so a turn without Phasing keeps the
        // economy block byte-identical and the client renders Phase buttons only when legal.
        ...(currentPhasingOptions.length > 0 ? { phasingOptions: currentPhasingOptions } : {}),
        // why: WP-858 / D-24794 — project the usable Ritual Artifacts omit-when-absent (the
        // phasingOptions posture), so the client shows Use only when the move would be accepted.
        ...(currentRitualArtifactOptions.length > 0 ? { ritualArtifactOptions: currentRitualArtifactOptions } : {}),
        // why: WP-790 / D-24652 — omit-when-absent, so a turn with no restricted grant keeps the
        // economy block byte-identical.
        ...(restrictedAttack.length > 0 ? { restrictedAttack } : {}),
    };
    // --- 8. Project log ---
    // why: shallow copy prevents mutation of G.messages through UIState
    const log = [...gameState.messages];
    // why: WP-200 — mirror the `log` projection for structured events. Spread
    // copy prevents UIState consumers from mutating G.notableEvents; per-entry
    // payloads are plain JSON objects (primitives + arrays) so a shallow
    // top-level copy is sufficient.
    const notableEvents = [...gameState.notableEvents];
    // --- 9. Project progress counters ---
    // why: progress counters are required on every UIState (even pre-play)
    // so the HUD can render a stable shape. WP-067.
    const progress = buildProgressCounters(gameState);
    // --- 10. Project decks (counts only) ---
    // why: WP-128 / WP-014A determinism contract — counts only; the
    // next-card identity is NEVER projected.
    //
    // why: WP-135 graduates `heroDeckCount` from the WP-128 D-12806
    // safe-skip constant 0 to `gameState.heroDeck.length`. The hero deck
    // reservoir is now built at Game.setup() (buildHeroDeck) from
    // MatchSetupConfig.heroDeckIds and the locked rarity → copy-count map
    // (D-13501); recruitHero refills HQ slots from the reservoir front
    // (FIFO via refillHqSlot). The WP-128 safe-skip marker that lived on
    // this assignment is removed; assignment-site marker count drops from
    // 8 to 7 across this file (the line-14 JSDoc reference is unchanged).
    const heroDeckCount = gameState.heroDeck.length;
    const decks = {
        villainDeckCount: gameState.villainDeck.deck.length,
        heroDeckCount,
        // why: WP-606 / D-24417 — villain-deck composition: the order-stripped
        // (sorted) multiset of the undrawn villain cards. `[...deck]` copies before
        // sorting so G is never mutated; the sort strips ORDER so the next reveal
        // stays unpredictable. PUBLIC — the remaining composition is derivable from
        // the public setup minus the public revealed discard. Projection-only:
        // never stored on G, no state-hash surface.
        villainDeckComposition: [...gameState.villainDeck.deck].sort(),
    };
    // --- 11. Project shared piles (counts only, plus the Officer card face) ---
    const horrorsCount = gameState.piles.horrors.length;
    const piles = {
        bystandersCount: gameState.piles.bystanders.length,
        woundsCount: gameState.piles.wounds.length,
        horrorsCount,
        officersCount: gameState.piles.officers.length,
        sidekicksCount: gameState.piles.sidekicks.length,
        // why: WP-648 follow-on — the Officers cell is the one recruitable supply
        // pile; projecting its card face lets the client render the actual
        // S.H.I.E.L.D. Officer card instead of a bare count so players see it is a
        // shop. resolveDisplay returns a FRESH shallow copy (never a G alias) and
        // falls back to the UNKNOWN placeholder if the display entry is absent
        // (narrow test mocks), matching every other display projection in this file.
        officerDisplay: resolveDisplay(SHIELD_OFFICER_EXT_ID, gameState),
        // why: WP-842 / D-24776 — the Sidekicks cell is the second recruitable supply pile; its
        // card face is projected the same way (fresh copy, UNKNOWN fallback) and always built.
        sidekickDisplay: resolveDisplay(SIDEKICK_EXT_ID, gameState),
    };
    // --- 12. Project KO pile ---
    // why: WP-128 / D-12804 — KO pile is shared and face-up; full
    // visibility matches physical-table semantics. Source path is
    // top-level `G.ko: CardExtId[]` per `types.ts:481`. The pre-flight
    // 2026-05-03 PS-1 correction (away from a non-existent nested path
    // under `G.piles`) is documented in D-12804 + the drift test
    // comment block — not repeated here so the EC §5 grep gate sees a
    // clean projection file. `topCard` is the last entry (`null` when
    // count === 0); `cards` is the full pile in deterministic insertion
    // order. WP-111 D-11105 aliasing-defense via per-entry shallow copy
    // in buildDisplayEntries.
    const koCardCount = gameState.ko.length;
    let koTopCard;
    if (koCardCount === 0) {
        koTopCard = null;
    }
    else {
        const topExtId = gameState.ko[koCardCount - 1];
        koTopCard = {
            extId: topExtId,
            display: resolveDisplay(topExtId, gameState),
        };
    }
    const koPile = {
        count: koCardCount,
        topCard: koTopCard,
        cards: buildDisplayEntries(gameState.ko, gameState),
    };
    // why: WP-664 / D-24475 — project the Transform second-form side deck
    // (G.transformDeck, WP-657 / D-24468) as public face-up display entries so the
    // client can render the separate transform deck. Always built (empty for a
    // non-transform game); passed through filterUIStateForAudience unredacted
    // (face-up shared-board data, like koPile.cards). buildDisplayEntries does the
    // per-entry shallow copy so the projection never aliases G's array.
    const transformDeck = buildDisplayEntries(gameState.transformDeck, gameState);
    const notableEventCards = buildNotableEventCards(gameState);
    // --- 13. Project pending hero choice ---
    // why: D-22201 + WP-222 — resolveDisplay() produces a fresh shallow copy of
    // the card's display data so the returned UIPendingHeroChoice object does NOT
    // alias any entry in G.cardDisplayData (aliasing defense per WP-111 D-11105).
    // Direct spread of G.pendingHeroChoice is forbidden by the EC-254 strict
    // 4-field projection contract — choiceType/cardId/playerID are copied
    // verbatim from G; display is resolved fresh via resolveDisplay().
    let pendingHeroChoice;
    if (gameState.pendingHeroChoice !== undefined) {
        const resolvedDisplay = resolveDisplay(gameState.pendingHeroChoice.cardId, gameState);
        pendingHeroChoice = {
            choiceType: gameState.pendingHeroChoice.choiceType,
            cardId: gameState.pendingHeroChoice.cardId,
            playerID: gameState.pendingHeroChoice.playerID,
            display: resolvedDisplay,
        };
    }
    // --- 13b. Project pending KO-a-Hero choice (front of queue) ---
    // why: D-24010 + WP-243 — project the FRONT entry of G.pendingKoHeroChoices
    // with its eligible targets recomputed fresh from current G (no snapshot —
    // WP-242 stores none). The eligible list holds the chooser's non-wound cards
    // in the entry's allowed zones, in array index order, deduped per (zone, cardId)
    // by buildKoEligibleTargets. `remaining` = total KOs owed = Σ (entry.remaining ??
    // 1) over the queue (WP-492 / D-24298): a queue of single-KO entries reads its
    // length unchanged (each owes 1), and a magnitude-N park (Whirlwind, one entry
    // with `remaining: 2`) reads 2.
    // resolveDisplay is spread fresh per entry so the projection holds no
    // reference into G.cardDisplayData (aliasing defense, WP-111 D-11105).
    // Redaction to the chooser-only audience is enforced by
    // filterUIStateForAudience (D-24011 — the eligible list carries hand
    // identities and must not leak to opponents/spectators).
    let pendingKoHeroChoice;
    if (gameState.pendingKoHeroChoices !== undefined && gameState.pendingKoHeroChoices.length > 0) {
        const frontChoice = gameState.pendingKoHeroChoices[0];
        const chooserZones = gameState.playerZones[frontChoice.playerID];
        if (chooserZones !== undefined) {
            // why: WP-577 — a hand-scoped entry (Red Skull) projects hand Heroes only, so
            // the prompt offers exactly the printed "from their hand" targets. Absent
            // `zones` ⇒ "your Heroes", hand + inPlay with no discard card (D-24687).
            // why: WP-836 / D-24769 — a filtered entry (Destroyer, 'non-grey') projects only the
            // cards its resolve accepts.
            const greyFilterRead = frontChoice.greyFilter === undefined ? undefined : { gameState, greyFilter: frontChoice.greyFilter };
            const eligibleTargets = buildKoEligibleTargets(chooserZones, frontChoice.zones, greyFilterRead);
            const eligible = [];
            for (const target of eligibleTargets) {
                eligible.push({
                    zone: target.zone,
                    cardId: target.cardId,
                    display: { ...resolveDisplay(target.cardId, gameState) },
                });
            }
            // why: WP-492 / D-24298 — total KOs owed across the queue, not entry count.
            let remaining = 0;
            for (const choice of gameState.pendingKoHeroChoices) {
                remaining += choice.remaining ?? 1;
            }
            pendingKoHeroChoice = {
                choiceType: frontChoice.choiceType,
                playerID: frontChoice.playerID,
                eligible,
                remaining,
            };
        }
    }
    // --- 13b.2 Project pending Doombot scry-KO choice (front of queue) ---
    // why: WP-470 / D-24282 — project the FRONT entry of G.pendingScryKoChoices with
    // its `revealedCardIds` snapshot (the top two deck cards, captured at park time)
    // resolved to display data, in deck-top order. Unlike the ko-hero projection this
    // reads the SNAPSHOT, not the live zone (D-24282): the block-all guard freezes the
    // deck top while pending, so the snapshot cannot drift, and it is exactly what the
    // resolve move validates the client's { cardId } against (the round-trip rule).
    // resolveDisplay is spread fresh per entry so the projection holds no reference into
    // G.cardDisplayData (aliasing defense, WP-111 D-11105). Redaction to the chooser-only
    // audience is enforced by filterUIStateForAudience (the revealed cards are the top of
    // the chooser's own deck — their next draws — and must not leak to opponents/spectators).
    let pendingScryKoChoice;
    if (gameState.pendingScryKoChoices !== undefined && gameState.pendingScryKoChoices.length > 0) {
        const frontChoice = gameState.pendingScryKoChoices[0];
        const revealedCards = [];
        for (const cardId of frontChoice.revealedCardIds) {
            revealedCards.push({
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingScryKoChoice = {
            choiceType: frontChoice.choiceType,
            playerID: frontChoice.playerID,
            revealedCards,
        };
    }
    // --- 13b.3 Project pending Melter Fight KO/keep choice (front of queue) ---
    // why: WP-603 / D-24413 — project the FRONT entry of G.pendingMelterKoChoices with
    // each player's revealed deck top (the snapshot captured at park time) resolved to
    // display data, in reveal (sorted player-id) order. Like the scry-ko projection this
    // reads the SNAPSHOT, not the live zone: the block-all guard freezes every deck top
    // while pending, so it cannot drift, and it is exactly what resolveMelterKoChoice
    // validates the client's { ownerPlayerID, cardId } against (the round-trip rule).
    // resolveDisplay is spread fresh per entry so the projection holds no reference into
    // G.cardDisplayData (aliasing defense, WP-111 D-11105). Redaction to the chooser-only
    // audience is enforced by filterUIStateForAudience (the revealed cards are the tops
    // of players' own decks — hidden next-draw information).
    let pendingMelterKoChoice;
    if (gameState.pendingMelterKoChoices !== undefined && gameState.pendingMelterKoChoices.length > 0) {
        const frontChoice = gameState.pendingMelterKoChoices[0];
        const revealedTops = [];
        for (const entry of frontChoice.revealedTops) {
            revealedTops.push({
                ownerPlayerID: entry.ownerPlayerID,
                cardId: entry.cardId,
                display: { ...resolveDisplay(entry.cardId, gameState) },
            });
        }
        pendingMelterKoChoice = {
            choiceType: frontChoice.choiceType,
            playerID: frontChoice.playerID,
            revealedTops,
        };
    }
    // why: WP-702 / D-24521 — project the FRONT entry of G.pendingRevealTopDispose with each
    // revealed deck top (the snapshot captured at park time) resolved to display data, in
    // reveal (sorted player-id) order. Reads the SNAPSHOT, not the live zone (the Melter
    // precedent): the block-all guard freezes every revealed deck top between moves (a stale
    // KO-or-keep snapshot is re-revealed engine-side by refreshStaleKoOrKeepFront, WP-754 /
    // D-24581, so the projected snapshot is current), and it is exactly what resolveRevealTopDispose
    // validates the client's
    // { ownerPlayerID, cardId } against (the round-trip rule). resolveDisplay is spread fresh
    // per entry so the projection holds no reference into G.cardDisplayData (WP-111 D-11105).
    // Redaction to the chooser-only audience is enforced by filterUIStateForAudience.
    let pendingRevealTopDispose;
    if (gameState.pendingRevealTopDispose !== undefined && gameState.pendingRevealTopDispose.length > 0) {
        const frontChoice = gameState.pendingRevealTopDispose[0];
        const revealedTops = [];
        for (const entry of frontChoice.revealedTops) {
            revealedTops.push({
                ownerPlayerID: entry.ownerPlayerID,
                cardId: entry.cardId,
                display: { ...resolveDisplay(entry.cardId, gameState) },
                // why: D-24558 — omit-when-off so an un-unlocked entry projects byte-identically.
                ...(entry.isKoAllowed === true ? { isKoAllowed: true } : {}),
                // why: WP-754 / D-24581 — omit-when-absent: only a KO-or-keep entry projects `false`.
                ...(entry.isDiscardAllowed === false ? { isDiscardAllowed: false } : {}),
            });
        }
        pendingRevealTopDispose = {
            choiceType: frontChoice.choiceType,
            playerID: frontChoice.playerID,
            revealedTops,
        };
    }
    // --- 13b.3b Project pending Ruthless Dictator scry-3 choice (front of queue) ---
    // why: WP-695 / D-24512 — project the FRONT entry of G.pendingRuthlessDictatorChoices
    // with its remaining revealed deck-top cards (the snapshot captured at park time,
    // shrinking as each is resolved) resolved to display data, plus the still-available
    // dispositions. Reads the SNAPSHOT, not the live zone: the block-all guard freezes the
    // deck top while pending, and it is exactly what resolveRuthlessDictatorChoice validates
    // the client's { cardId, disposition } against (the round-trip rule). resolveDisplay is
    // spread fresh per entry so the projection holds no reference into G.cardDisplayData
    // (aliasing defense, WP-111 D-11105). Redaction to the chooser-only audience is enforced
    // by filterUIStateForAudience (the revealed cards are the top of the chooser's own deck).
    let pendingRuthlessDictatorChoice;
    if (gameState.pendingRuthlessDictatorChoices !== undefined &&
        gameState.pendingRuthlessDictatorChoices.length > 0) {
        const frontChoice = gameState.pendingRuthlessDictatorChoices[0];
        const revealedCards = [];
        for (const cardId of frontChoice.revealedCardIds) {
            revealedCards.push({
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingRuthlessDictatorChoice = {
            choiceType: frontChoice.choiceType,
            playerID: frontChoice.playerID,
            revealedCards,
            availableDispositions: [...frontChoice.availableDispositions],
        };
    }
    // --- 13b.3b2 Project pending reveal-three assignment (front of queue) ---
    // why: WP-753 / D-24580 — project the FRONT entry of G.pendingRevealThreeAssign with its
    // remaining revealed deck-top cards (the snapshot captured at reveal time, shrinking as each is
    // assigned) resolved to display data, the unused disposition slots, the source card and the
    // repeat counter. Reads the SNAPSHOT, not the live zone: the block-all guard freezes the deck top
    // while pending, and it is exactly what resolveRevealThreeAssign validates the client's
    // { cardId, disposition } against (the round-trip rule). resolveDisplay is spread fresh per entry
    // (aliasing defense, WP-111 D-11105). Chooser-only redaction is enforced by
    // filterUIStateForAudience.
    let pendingRevealThreeAssign;
    if (gameState.pendingRevealThreeAssign !== undefined &&
        gameState.pendingRevealThreeAssign.length > 0) {
        const frontEntry = gameState.pendingRevealThreeAssign[0];
        const revealedCards = [];
        for (const cardId of frontEntry.revealedCardIds) {
            revealedCards.push({
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingRevealThreeAssign = {
            choiceType: frontEntry.choiceType,
            playerID: frontEntry.playerID,
            sourceCard: {
                cardId: frontEntry.sourceCardId,
                display: { ...resolveDisplay(frontEntry.sourceCardId, gameState) },
            },
            revealedCards,
            availableDispositions: [...frontEntry.availableDispositions],
            remainingRepeats: frontEntry.remainingRepeats,
        };
    }
    // --- 13b.3c Project pending Electromagnetic Bubble X-Men pick (front of queue) ---
    // why: WP-695 / D-24512 — project the FRONT entry of G.pendingElectromagneticBubbleChoices
    // with its eligible X-Men Heroes (the snapshot captured at park time) resolved to
    // display data, in in-play order. Reads the SNAPSHOT, not the live zone: the block-all
    // guard freezes the board while pending, and it is exactly what
    // resolveElectromagneticBubbleChoice validates the client's { cardId } against (the
    // round-trip rule). resolveDisplay is spread fresh per entry (aliasing defense). Redaction
    // to the chooser-only audience is enforced by filterUIStateForAudience.
    let pendingElectromagneticBubbleChoice;
    if (gameState.pendingElectromagneticBubbleChoices !== undefined &&
        gameState.pendingElectromagneticBubbleChoices.length > 0) {
        const frontChoice = gameState.pendingElectromagneticBubbleChoices[0];
        const eligibleCards = [];
        for (const cardId of frontChoice.eligibleCardIds) {
            eligibleCards.push({
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingElectromagneticBubbleChoice = {
            choiceType: frontChoice.choiceType,
            playerID: frontChoice.playerID,
            eligibleCards,
        };
    }
    // --- 13b.3 Project pending discard-to-limit choice (front of queue) ---
    // why: WP-476 / D-24284 — project the FRONT entry of G.pendingDiscardChoices with
    // the chooser's CURRENT hand (recomputed fresh from G — the pending entry stores no
    // snapshot; the block-all guard freezes the hand while pending) resolved to display
    // data, in hand order, plus the limit. The client requires exactly
    // `hand.length - limit` selections. resolveDisplay is spread fresh per entry so the
    // projection holds no reference into G.cardDisplayData (aliasing defense, WP-111
    // D-11105). Redaction to the chooser-only audience is enforced by
    // filterUIStateForAudience (D-24011 — the hand carries the chooser's private card
    // identities and must not leak to opponents/spectators).
    let pendingDiscardChoice;
    if (gameState.pendingDiscardChoices !== undefined && gameState.pendingDiscardChoices.length > 0) {
        const frontChoice = gameState.pendingDiscardChoices[0];
        const chooserZones = gameState.playerZones[frontChoice.playerID];
        if (chooserZones !== undefined) {
            const hand = [];
            for (const cardId of chooserZones.hand) {
                hand.push({
                    cardId,
                    display: { ...resolveDisplay(cardId, gameState) },
                });
            }
            pendingDiscardChoice = {
                choiceType: frontChoice.choiceType,
                playerID: frontChoice.playerID,
                limit: frontChoice.limit,
                hand,
            };
        }
    }
    // why: WP-538 / D-24347 — project the FRONT entry of G.pendingPutCardsOnDeckChoices with
    // the chooser's CURRENT hand (recomputed fresh from G — the pending entry stores no
    // snapshot; the block-all guard freezes the hand while pending) resolved to display data,
    // in hand order, plus the count. The client requires exactly `count` selections in top
    // order. resolveDisplay is spread fresh per entry so the projection holds no reference into
    // G.cardDisplayData (aliasing defense, WP-111 D-11105). Redaction to the chooser-only
    // audience is enforced by filterUIStateForAudience (the hand carries the chooser's private
    // card identities and must not leak to opponents/spectators).
    let pendingPutCardsOnDeckChoice;
    if (gameState.pendingPutCardsOnDeckChoices !== undefined && gameState.pendingPutCardsOnDeckChoices.length > 0) {
        const frontChoice = gameState.pendingPutCardsOnDeckChoices[0];
        const chooserZones = gameState.playerZones[frontChoice.playerID];
        if (chooserZones !== undefined) {
            const hand = [];
            for (const cardId of chooserZones.hand) {
                hand.push({
                    cardId,
                    display: { ...resolveDisplay(cardId, gameState) },
                });
            }
            pendingPutCardsOnDeckChoice = {
                choiceType: frontChoice.choiceType,
                playerID: frontChoice.playerID,
                count: frontChoice.count,
                hand,
            };
        }
    }
    // why: WP-479 / D-24286 — project the FRONT entry of G.pendingReorderChoices with its
    // `cardIds` snapshot (the revealed remainder on top of the deck, captured at park time)
    // resolved to display data, in current deck-top order. Reads the SNAPSHOT, not the live
    // zone (mirrors scry-KO, D-24282): the block-all guard freezes the deck top while pending,
    // and it is exactly what resolveReorderChoice validates the client's { orderedCardIds }
    // permutation against (the round-trip rule). resolveDisplay is spread fresh per entry
    // (aliasing defense, WP-111 D-11105). Redaction to the chooser-only audience is enforced
    // by filterUIStateForAudience (the remainder is the top of the chooser's own deck — their
    // next draws — and must not leak to opponents/spectators).
    let pendingReorderChoice;
    if (gameState.pendingReorderChoices !== undefined && gameState.pendingReorderChoices.length > 0) {
        const frontChoice = gameState.pendingReorderChoices[0];
        const cards = [];
        for (const cardId of frontChoice.cardIds) {
            cards.push({
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingReorderChoice = {
            choiceType: frontChoice.choiceType,
            playerID: frontChoice.playerID,
            cards,
        };
    }
    // why: WP-486 / D-24291 — project the FRONT entry of G.pendingDefeatChoices with its
    // eligible-target snapshot (City Villains holding a Bystander + the Mastermind, in
    // deterministic order), each resolved to display data for the prompt. The block-all
    // guard freezes the board while pending, so the snapshot still names the exact targets
    // resolveDefeatChoice validates the client's { targetKind, cityIndex } against (the
    // round-trip rule). resolveDisplay is spread fresh per target (aliasing defense,
    // WP-111 D-11105). Redaction to the chooser-only audience is enforced by
    // filterUIStateForAudience (it is the chooser's pending decision, D-24011).
    let pendingDefeatChoice;
    if (gameState.pendingDefeatChoices !== undefined && gameState.pendingDefeatChoices.length > 0) {
        const frontChoice = gameState.pendingDefeatChoices[0];
        const targets = [];
        for (const target of frontChoice.targets) {
            targets.push({
                kind: target.kind,
                // why: cityIndex is present only for a villain target; conditional spread
                // avoids an explicit `undefined` under exactOptionalPropertyTypes.
                ...(target.cityIndex !== undefined ? { cityIndex: target.cityIndex } : {}),
                display: { ...resolveDisplay(target.cardId, gameState) },
            });
        }
        pendingDefeatChoice = {
            choiceType: frontChoice.choiceType,
            playerID: frontChoice.playerID,
            targets,
        };
    }
    // why: WP-693 / D-24510 — project the FRONT entry of G.pendingKoDiscardChoices with the
    // chooser's CURRENT discard (recomputed fresh from G — the pending entry stores no
    // snapshot; the block-all guard freezes the discard while pending) resolved to display
    // data, in discard order, plus the cap. The client picks 0..maxCount DISTINCT cards.
    // resolveDisplay is spread fresh per entry so the projection holds no reference into
    // G.cardDisplayData (aliasing defense, WP-111 D-11105). Redaction to the chooser-only
    // audience is enforced by filterUIStateForAudience (it is the chooser's pending decision).
    let pendingKoDiscardChoice;
    if (gameState.pendingKoDiscardChoices !== undefined && gameState.pendingKoDiscardChoices.length > 0) {
        const frontChoice = gameState.pendingKoDiscardChoices[0];
        const chooserZones = gameState.playerZones[frontChoice.playerID];
        if (chooserZones !== undefined) {
            const discard = [];
            // why: WP-836 / D-24769 — the eligibility helper the resolve shares, so a grey-only
            // entry (Destroyer / Frost Giant Warrior / Ymir) lists only the grey Heroes; an
            // unfiltered entry still lists the whole discard.
            // why: WP-850 / D-24786 — an entry limited to the cards one play just discarded (Firepower
            // from the Future) lists only those still in the discard, via the same helper.
            for (const cardId of getEligibleKoDiscardCards(gameState, frontChoice.playerID, frontChoice.greyFilter, frontChoice.eligibleCardIds)) {
                discard.push({
                    cardId,
                    display: { ...resolveDisplay(cardId, gameState) },
                });
            }
            pendingKoDiscardChoice = {
                choiceType: frontChoice.choiceType,
                playerID: frontChoice.playerID,
                maxCount: frontChoice.maxCount,
                // why: D-24783 — the filter rides along so the prompt can name grey Heroes; omitted
                // (never an `undefined` literal) on an unfiltered entry.
                ...(frontChoice.greyFilter !== undefined ? { greyFilter: frontChoice.greyFilter } : {}),
                // why: WP-850 / D-24786 — tells the prompt the list is "the cards you just discarded";
                // omitted (never an `undefined` or `false` literal) on every other entry.
                ...(frontChoice.eligibleCardIds !== undefined ? { isLimitedToDiscardedThisWay: true } : {}),
                discard,
            };
        }
    }
    // --- 13c. Project pending optional-KO-then-reward choice (front of queue) ---
    // why: D-24020 + WP-249 — project the FRONT entry of G.pendingOptionalKoRewards
    // with the chooser's eligible hand + discard cards recomputed fresh from current
    // G (no snapshot — WP-248 stores none). The eligible lists mirror
    // G.playerZones[pid].hand / .discard in zone + index order with NO pre-filter
    // and NO reorder, so the client's { zone, cardId } selection always maps to a
    // card the engine resolve accepts (the round-trip rule — a filtered/reordered
    // list lets the client submit a card the resolve rejects). resolveDisplay is
    // spread fresh per entry so the projection holds no reference into
    // G.cardDisplayData (aliasing defense, WP-111 D-11105). The reward label is
    // derived once by deriveOptionalKoRewardLabel (the single mapping). Redaction
    // to the chooser-only audience is enforced by filterUIStateForAudience
    // (D-24011 analog — the eligible lists carry the chooser's private hand/discard).
    let pendingOptionalKoReward;
    if (gameState.pendingOptionalKoRewards !== undefined &&
        gameState.pendingOptionalKoRewards.length > 0) {
        const frontReward = gameState.pendingOptionalKoRewards[0];
        const chooserZones = gameState.playerZones[frontReward.playerID];
        if (chooserZones !== undefined) {
            // why: WP-681 / D-24498 — Battlefield Promotion (koTeamFilter 'shield') restricts the
            // KO target to S.H.I.E.L.D. Heroes, so the eligible lists must list ONLY S.H.I.E.L.D.
            // Heroes (the client submits only what it sees; the resolve rejects a non-shield target
            // too, but a filtered projection is what a well-behaved client offers). Absent
            // koTeamFilter = no restriction, so every existing entry lists every card unchanged.
            const shieldOnly = frontReward.koTeamFilter === 'shield';
            // why: WP-767 / D-24600 — koHeroesOnly (Snarling Fangs' "one of your Heroes") omits
            // Wounds from all three lists, matching the resolve's Wound rejection. Absent = no
            // filter, so every existing entry lists every card unchanged.
            const heroesOnly = frontReward.koHeroesOnly === true;
            // why: WP-826 / D-24759 — mirrors the discard / inPlay gates below: list hand cards ONLY
            // when the entry's koZones permits hand. Reassign to Civilian Duty's entry sets ['inPlay']
            // (a Hero you played this turn), so its hand list is empty. Absent koZones = the wide set,
            // and every existing koZones includes 'hand', so existing entries project hand unchanged.
            const handPermitted = frontReward.koZones === undefined || frontReward.koZones.includes('hand');
            const eligibleHand = [];
            if (handPermitted) {
                for (const cardId of chooserZones.hand) {
                    if (shieldOnly && !cardCountsAsShieldHero(gameState, cardId)) {
                        continue;
                    }
                    if (heroesOnly && cardId === WOUND_EXT_ID) {
                        continue;
                    }
                    eligibleHand.push({
                        zone: 'hand',
                        cardId,
                        display: { ...resolveDisplay(cardId, gameState) },
                    });
                }
            }
            // why: WP-767 / D-24600 — mirrors the inPlay gate below: list discard cards ONLY when
            // the entry's koZones permits discard. Snarling Fangs' entry sets ['hand','inPlay'], so
            // its discard list is empty and the chooser is never offered a card the resolve rejects.
            // Absent koZones = the wide set, so existing entries project discard exactly as before.
            const discardPermitted = frontReward.koZones === undefined || frontReward.koZones.includes('discard');
            const eligibleDiscard = [];
            if (discardPermitted) {
                for (const cardId of chooserZones.discard) {
                    if (shieldOnly && !cardCountsAsShieldHero(gameState, cardId)) {
                        continue;
                    }
                    if (heroesOnly && cardId === WOUND_EXT_ID) {
                        continue;
                    }
                    eligibleDiscard.push({
                        zone: 'discard',
                        cardId,
                        display: { ...resolveDisplay(cardId, gameState) },
                    });
                }
            }
            // why: D-24442 — cards the chooser played this turn (inPlay) are a valid KO
            // source; project them in zone+index order with a fresh display spread,
            // exactly like the hand/discard lists above (round-trip rule + aliasing
            // defense). Redacted to the chooser only by filterUIStateForAudience.
            // why: WP-667 / D-24480 — but ONLY when the entry's koZones permits inPlay.
            // Radioactive Riot's no-reward entry sets koZones ['hand','discard'] (the card
            // says "hand or discard pile", not "played this turn"), so its inPlay list is
            // empty — the chooser is never offered an in-play card. Absent koZones = the
            // wide set, so existing rewarded entries project inPlay exactly as before.
            const inPlayPermitted = frontReward.koZones === undefined || frontReward.koZones.includes('inPlay');
            const eligibleInPlay = [];
            if (inPlayPermitted) {
                for (const cardId of chooserZones.inPlay) {
                    // why: WP-826 / D-24759 — Reassign to Civilian Duty is the first shield-only entry that
                    // permits inPlay, so the S.H.I.E.L.D. filter applies here too (the resolve rejects a
                    // non-shield played card). Every earlier shield-only entry omits inPlay, so no change.
                    if (shieldOnly && !cardCountsAsShieldHero(gameState, cardId)) {
                        continue;
                    }
                    if (heroesOnly && cardId === WOUND_EXT_ID) {
                        continue;
                    }
                    eligibleInPlay.push({
                        zone: 'inPlay',
                        cardId,
                        display: { ...resolveDisplay(cardId, gameState) },
                    });
                }
            }
            pendingOptionalKoReward = {
                playerID: frontReward.playerID,
                rewardLabel: deriveOptionalKoRewardLabel(frontReward.rewardType, frontReward.rewardMagnitude),
                eligibleHand,
                eligibleDiscard,
                eligibleInPlay,
            };
        }
    }
    // --- 13c.2 Project pending draw-or-empowered choice (front of queue) ---
    // why: D-24071 + WP-287 — project the FRONT entry of G.pendingDrawOrEmpowered with
    // the empoweredLabel derived once by deriveEmpoweredLabel (the single class→display
    // mapping). Recomputed fresh from current G (WP-286 stores no snapshot). Simpler than
    // the optional-KO-reward projection — a binary choice with no eligible-card list.
    // Redaction to the chooser-only audience is enforced by filterUIStateForAudience
    // (D-24011 analog — the choice is private to the chooser, keyed on .playerID).
    let pendingDrawOrEmpowered;
    if (gameState.pendingDrawOrEmpowered !== undefined &&
        gameState.pendingDrawOrEmpowered.length > 0) {
        const frontChoice = gameState.pendingDrawOrEmpowered[0];
        pendingDrawOrEmpowered = {
            playerID: frontChoice.playerID,
            empoweredLabel: deriveEmpoweredLabel(frontChoice.empoweredClass),
        };
    }
    // why: WP-719 / D-24541 — project the FRONT entry of G.pendingCoveringFireChoices as a binary
    // Covering Fire choice. otherPlayerCount is derived from the live G (all seats minus the
    // chooser), so the prompt can label "each of N other players". Recomputed fresh from current G
    // (WP-719 stores no snapshot). Redaction to the chooser-only audience is enforced by
    // filterUIStateForAudience (D-24011 analog — the choice is private to the chooser, keyed on
    // .playerID).
    let pendingCoveringFireChoice;
    if (gameState.pendingCoveringFireChoices !== undefined &&
        gameState.pendingCoveringFireChoices.length > 0) {
        const frontChoice = gameState.pendingCoveringFireChoices[0];
        // why: count OTHER seats (all players minus the chooser) for the prompt label; never a
        // hardcoded player count (a match may be 1..N players).
        const otherPlayerCount = Object.keys(gameState.playerZones).filter((seatId) => seatId !== frontChoice.playerID).length;
        pendingCoveringFireChoice = {
            playerID: frontChoice.playerID,
            otherPlayerCount,
        };
    }
    // why: WP-795 / D-24664 — project the FRONT entry of G.pendingMoveVillainChoices as the
    // Spinning Cyclone move-a-Villain prompt. villainCityIndices is recomputed from the LIVE G.city
    // on every build into a fresh array (ascending non-null indices) — nothing is snapshotted at
    // park time, so the prompt always offers the Villains actually in the City. Redaction to the
    // chooser-only audience is enforced by filterUIStateForAudience (keyed on .playerID).
    let pendingMoveVillainChoice;
    if (gameState.pendingMoveVillainChoices !== undefined &&
        gameState.pendingMoveVillainChoices.length > 0) {
        const frontChoice = gameState.pendingMoveVillainChoices[0];
        const villainCityIndices = [];
        for (let cityIndex = 0; cityIndex < gameState.city.length; cityIndex++) {
            if (gameState.city[cityIndex] !== null) {
                villainCityIndices.push(cityIndex);
            }
        }
        pendingMoveVillainChoice = {
            playerID: frontChoice.playerID,
            villainCityIndices,
        };
    }
    // why: WP-724 / D-24546 — project the FRONT entry of G.pendingSplitFaceChoices as the two-face
    // "choose a side" picker. Each face's name/cost/abilityText comes from G.cardDisplayData and its
    // attack/recruit from G.cardStats (both faces' entries live in G per D-24545), recomputed fresh
    // from current G (no snapshot). Redaction to the chooser-only audience is enforced by
    // filterUIStateForAudience (keyed on .playerID). The two faces are inlined (duplicate-first, not
    // abstracted — only two uses) per the 00.6 no-premature-abstraction rule.
    let pendingSplitFaceChoice;
    if (gameState.pendingSplitFaceChoices !== undefined &&
        gameState.pendingSplitFaceChoices.length > 0) {
        const frontSplit = gameState.pendingSplitFaceChoices[0];
        const faceADisplay = gameState.cardDisplayData[frontSplit.faceA];
        const faceAStats = gameState.cardStats[frontSplit.faceA];
        const faceA = {
            extId: frontSplit.faceA,
            name: faceADisplay?.name ?? '',
            cost: faceADisplay?.cost ?? null,
            attack: faceAStats?.attack ?? 0,
            recruit: faceAStats?.recruit ?? 0,
            // why: WP-777 / D-24615 — always set: whether the engine accepts this side now, and its
            // discard cost, so the picker can disable an unpayable side and say why (WP-778).
            isSelectable: isSplitFaceBindable(gameState, frontSplit.playerID, frontSplit.faceA, frontSplit.faceB),
            discardToPlayCost: getDiscardToPlayCost(gameState, frontSplit.faceA),
        };
        if (faceADisplay?.abilityText !== undefined) {
            faceA.abilityText = faceADisplay.abilityText;
        }
        const faceBDisplay = gameState.cardDisplayData[frontSplit.faceB];
        const faceBStats = gameState.cardStats[frontSplit.faceB];
        const faceB = {
            extId: frontSplit.faceB,
            name: faceBDisplay?.name ?? '',
            cost: faceBDisplay?.cost ?? null,
            attack: faceBStats?.attack ?? 0,
            recruit: faceBStats?.recruit ?? 0,
            isSelectable: isSplitFaceBindable(gameState, frontSplit.playerID, frontSplit.faceB, frontSplit.faceA),
            discardToPlayCost: getDiscardToPlayCost(gameState, frontSplit.faceB),
        };
        if (faceBDisplay?.abilityText !== undefined) {
            faceB.abilityText = faceBDisplay.abilityText;
        }
        // why: printed left-to-right order for the picker — faceB is the left half when the split
        // card's primary card-key (faceA minus its #copy suffix) is in G.splitFacesAlternateOnLeft.
        const faceABaseKey = splitInstanceIntoBaseAndCopy(frontSplit.faceA).baseKey;
        const leftFace = gameState.splitFacesAlternateOnLeft?.[faceABaseKey] === true ? 'b' : 'a';
        pendingSplitFaceChoice = { playerID: frontSplit.playerID, faceA, faceB, leftFace };
    }
    // why: WP-675 / D-24490 — project the FRONT entry of G.pendingCountScaledChoice, resolving
    // each option's count/total from the live G (resolveCountSource, excluding the triggering
    // card) so the chooser sees each button's concrete grant. Recomputed fresh from current G
    // (no snapshot). Redaction to the chooser-only audience is enforced by filterUIStateForAudience.
    let pendingCountScaledChoice;
    if (gameState.pendingCountScaledChoice !== undefined &&
        gameState.pendingCountScaledChoice.length > 0) {
        const frontChoice = gameState.pendingCountScaledChoice[0];
        pendingCountScaledChoice = {
            playerID: frontChoice.playerID,
            // why: WP-679 / D-24495 — project each option by kind. A count-scaled option resolves its
            // count live (perEach floor-division, matching the executor); an Undercover option carries
            // only a display label (its target is picked at resolve time, possibly via a nested choice).
            options: frontChoice.options.map((option) => {
                if (option.kind === 'undercover') {
                    return {
                        kind: 'undercover',
                        label: option.source === 'hand-shield-hero'
                            ? 'Send a S.H.I.E.L.D. Hero from your hand Undercover'
                            : 'Send a card from the S.H.I.E.L.D. Officer Stack Undercover',
                    };
                }
                // why: D-24696 — Spy Network's two single-line options. The Officer gain carries only a
                // label (an empty stack still shows the printed option; choosing it is a logged no-op);
                // the flat attack carries its fixed grant as `total`.
                if (option.kind === 'gain-officer') {
                    return {
                        kind: 'gain-officer',
                        label: 'Gain a S.H.I.E.L.D. Officer',
                    };
                }
                if (option.kind === 'flat-attack') {
                    return {
                        kind: 'flat-attack',
                        resource: 'attack',
                        total: option.magnitude,
                        label: `+${option.magnitude} Attack`,
                    };
                }
                // why: WP-856 / D-24792 — the flat recruit option carries its fixed grant as `total`,
                // the flat-attack mirror.
                if (option.kind === 'flat-recruit') {
                    return {
                        kind: 'flat-recruit',
                        resource: 'recruit',
                        total: option.magnitude,
                        label: `+${option.magnitude} Recruit`,
                    };
                }
                const count = resolveCountSource(gameState, frontChoice.playerID, option.countSource, frontChoice.cardId);
                // why: WP-677/679 — perEach ("for each N") floor-divides the count, matching the grant.
                const perEach = option.perEach && option.perEach > 0 ? option.perEach : 1;
                const total = option.magnitude * Math.floor(count / perEach);
                return {
                    kind: 'count-scaled',
                    resource: option.resource,
                    perUnit: option.magnitude,
                    count,
                    total,
                    label: `+${total} ${option.resource === 'attack' ? 'Attack' : 'Recruit'}`,
                };
            }),
        };
    }
    // why: WP-678 / D-24494 — project the FRONT entry of G.pendingUndercoverChoice (its eligible
    // hand-Hero ext_ids) so the chooser can pick which Hero to send Undercover. Redaction to the
    // chooser-only audience is enforced by filterUIStateForAudience.
    let pendingUndercoverChoice;
    if (gameState.pendingUndercoverChoice !== undefined &&
        gameState.pendingUndercoverChoice.length > 0) {
        const frontUndercover = gameState.pendingUndercoverChoice[0];
        pendingUndercoverChoice = {
            playerID: frontUndercover.playerID,
            eligibleTargets: [...frontUndercover.eligibleTargets],
        };
    }
    // why: WP-684 / D-24501 — project G.pendingSeatChoice (the single open non-active/multi-seat
    // choice). The FULL projection carries every addressed seat's own prompt in seatPrompts;
    // filterUIStateForAudience redacts it to only the viewing seat's own entry (the per-seat
    // redaction contract). addressedSeats / outstandingSeats are seat-id liveness info (no private
    // data). Absent (undefined) means no pending seat choice. Never set by an existing game.
    let pendingSeatChoice;
    if (gameState.pendingSeatChoice !== undefined) {
        const seatChoice = gameState.pendingSeatChoice;
        const outstandingSeats = seatChoice.addressedSeats.filter((seat) => !Object.prototype.hasOwnProperty.call(seatChoice.submissions, seat));
        const seatPrompts = {};
        for (const seat of seatChoice.addressedSeats) {
            const prompt = seatChoice.seatPrompts[seat];
            seatPrompts[seat] = {
                options: (prompt?.options ?? []).map((option) => ({ label: option.label })),
            };
        }
        pendingSeatChoice = {
            kind: seatChoice.kind,
            addressedSeats: [...seatChoice.addressedSeats],
            outstandingSeats,
            seatPrompts,
        };
    }
    // why: WP-663 / D-24474 — project the FRONT entry of G.pendingPlayVillainTopChoices so
    // the chooser can render Shadowed Thoughts' "Play the top Villain-Deck card for +N Attack?"
    // prompt. Binary choice, no eligible-card list (mirrors pendingDrawOrEmpowered). Redaction
    // to the chooser-only audience is enforced by filterUIStateForAudience (keyed on .playerID).
    let pendingPlayVillainTop;
    if (gameState.pendingPlayVillainTopChoices !== undefined &&
        gameState.pendingPlayVillainTopChoices.length > 0) {
        const frontChoice = gameState.pendingPlayVillainTopChoices[0];
        pendingPlayVillainTop = {
            playerID: frontChoice.playerID,
            attackReward: frontChoice.attackReward,
        };
    }
    // --- 13c.3 Project pending victory-pile villain-pick choice (front of queue) ---
    // why: WP-313 / D-24099 — project the FRONT entry of G.pendingVictoryPileCardPick
    // with the eligible victory-pile villains recomputed fresh from current G via the
    // engine's getEligibleVictoryVillains (WP-285 stores no snapshot). The list mirrors
    // the resolve-time filter (victory pile ∩ villain or henchman type), in victory-pile order and
    // unfiltered, so the client's { cardId } selection always maps to a villain the
    // engine resolve accepts (the round-trip rule). Each entry's attackValue is the
    // villain's printed attack, read from G.cardStats[cardId].fightCost the same way the
    // resolve move reads it (a villain's .attack is always 0 — WP-285). resolveDisplay
    // is spread fresh so the projection holds no reference into G.cardDisplayData
    // (aliasing defense, WP-111 D-11105). Redaction to the chooser-only audience is
    // enforced by filterUIStateForAudience (D-24099 — keyed on .playerID).
    let pendingVictoryPileCardPick;
    if (gameState.pendingVictoryPileCardPick !== undefined &&
        gameState.pendingVictoryPileCardPick.length > 0) {
        const frontPick = gameState.pendingVictoryPileCardPick[0];
        const eligibleVillains = [];
        for (const cardId of getEligibleVictoryVillains(gameState, frontPick.playerID)) {
            const stats = gameState.cardStats[cardId];
            eligibleVillains.push({
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
                // why: WP-285 — the villain's printed [icon:attack] is stored as fightCost;
                // 0 when stats are somehow absent (never throws — the projection is read-only).
                attackValue: stats !== undefined ? stats.fightCost : 0,
            });
        }
        pendingVictoryPileCardPick = {
            playerID: frontPick.playerID,
            eligibleVillains,
        };
    }
    // --- 13c.4 Project pending optional-put-bottom-hq choice (front of queue) ---
    // why: project the FRONT entry of G.pendingOptionalPutBottomHQ with the eligible
    // HQ cards recomputed fresh from current G.hq (the non-null slots, in slot order)
    // — the same set the resolve move matches against, so the client's { cardId }
    // selection always round-trips. Each display is spread fresh so the projection
    // holds no reference into G.cardDisplayData (aliasing defense, WP-111 D-11105).
    // Redaction to the chooser-only audience is enforced by filterUIStateForAudience
    // (keyed on .playerID), mirroring pendingVictoryPileCardPick.
    let pendingOptionalPutBottomHQ;
    if (gameState.pendingOptionalPutBottomHQ !== undefined &&
        gameState.pendingOptionalPutBottomHQ.length > 0) {
        const frontChoice = gameState.pendingOptionalPutBottomHQ[0];
        const eligibleHqCards = [];
        for (const slot of gameState.hq) {
            if (slot !== null) {
                eligibleHqCards.push({
                    cardId: slot,
                    display: { ...resolveDisplay(slot, gameState) },
                });
            }
        }
        pendingOptionalPutBottomHQ = {
            playerID: frontChoice.playerID,
            eligibleHqCards,
            // why: D-24133 — surface the mandatory flag so the client hides Decline for the
            // "Put a card…" form (Absorb Ambient Power). Omit-when-false keeps the optional
            // form's projection byte-identical (no fixture churn).
            ...(frontChoice.mandatory === true ? { mandatory: true } : {}),
        };
    }
    // --- 13c.5 Project pending put-any-number-bottom-hq choice (front of queue) ---
    // why: D-24132 — the MULTI-select sibling of 13c.4. Project the FRONT entry of
    // G.pendingPutAnyNumberBottomHQ with the eligible HQ cards recomputed fresh from current
    // G.hq (the non-null slots, in slot order) — the same set the resolve move matches against,
    // so each { cardId } in the client's selection always round-trips. Each display is spread
    // fresh so the projection holds no reference into G.cardDisplayData (aliasing defense, WP-111
    // D-11105). Redaction to the chooser-only audience is enforced by filterUIStateForAudience
    // (keyed on .playerID), mirroring pendingOptionalPutBottomHQ.
    let pendingPutAnyNumberBottomHQ;
    if (gameState.pendingPutAnyNumberBottomHQ !== undefined &&
        gameState.pendingPutAnyNumberBottomHQ.length > 0) {
        const frontChoice = gameState.pendingPutAnyNumberBottomHQ[0];
        const eligibleHqCards = [];
        for (const slot of gameState.hq) {
            if (slot !== null) {
                eligibleHqCards.push({
                    cardId: slot,
                    display: { ...resolveDisplay(slot, gameState) },
                });
            }
        }
        pendingPutAnyNumberBottomHQ = {
            playerID: frontChoice.playerID,
            eligibleHqCards,
        };
    }
    // --- 13c.6 Project pending return-zero-cost-discard choice (front of queue) ---
    // why: D-24139 — project the FRONT entry of G.pendingReturnZeroCostDiscard with the
    // eligible 0-cost discard cards recomputed fresh from the chooser's current discard
    // pile via getEligibleZeroCostDiscardCards — the SAME predicate the resolve move
    // validates with, so the client's { cardId } selection always round-trips. Each
    // display is spread fresh so the projection holds no reference into
    // G.cardDisplayData (aliasing defense, WP-111 D-11105). Redaction to the
    // chooser-only audience is enforced by filterUIStateForAudience (keyed on
    // .playerID), mirroring pendingOptionalPutBottomHQ.
    let pendingReturnZeroCostDiscard;
    if (gameState.pendingReturnZeroCostDiscard !== undefined &&
        gameState.pendingReturnZeroCostDiscard.length > 0) {
        const frontChoice = gameState.pendingReturnZeroCostDiscard[0];
        const eligibleDiscardCards = [];
        for (const cardId of getEligibleZeroCostDiscardCards(gameState, frontChoice.playerID)) {
            eligibleDiscardCards.push({
                zone: 'discard',
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingReturnZeroCostDiscard = {
            playerID: frontChoice.playerID,
            eligibleDiscardCards,
        };
    }
    // --- 13c.7 Project pending discard-to-play cost (front of queue) ---
    // why: WP-383 / D-24184 — project the FRONT entry of G.pendingDiscardToPlay with the
    // eligible hand cards recomputed fresh via getEligibleDiscardToPlayCards — the SAME
    // predicate the resolve move validates with, so the client's { cardId } selection
    // always round-trips. Each display is spread fresh (aliasing defense, WP-111 D-11105).
    // Chooser-only redaction is enforced by filterUIStateForAudience (keyed on .playerID),
    // mirroring pendingReturnZeroCostDiscard.
    let pendingDiscardToPlay;
    if (gameState.pendingDiscardToPlay !== undefined &&
        gameState.pendingDiscardToPlay.length > 0) {
        const frontChoice = gameState.pendingDiscardToPlay[0];
        const eligibleDiscardCards = [];
        for (const cardId of getEligibleDiscardToPlayCards(gameState, frontChoice.playerID)) {
            eligibleDiscardCards.push({
                zone: 'hand',
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingDiscardToPlay = {
            playerID: frontChoice.playerID,
            remaining: frontChoice.remaining,
            eligibleDiscardCards,
        };
    }
    // why: WP-676 / D-24492 — project the FRONT entry of G.pendingSmashDiscards with the
    // eligible hand cards recomputed fresh via getEligibleSmashDiscardCards — the SAME
    // predicate the resolve move validates with, so the client's { cardId } selection always
    // round-trips. Each card lives in the chooser's hand (zone 'hand'); each display is spread
    // fresh (aliasing defense, WP-111 D-11105). `magnitude` carries the +N Attack a discard
    // grants (for the prompt label). Chooser-only redaction is enforced by
    // filterUIStateForAudience (keyed on .playerID), mirroring pendingDiscardToPlay.
    let pendingSmashDiscard;
    if (gameState.pendingSmashDiscards !== undefined &&
        gameState.pendingSmashDiscards.length > 0) {
        const frontChoice = gameState.pendingSmashDiscards[0];
        const eligibleHand = [];
        for (const cardId of getEligibleSmashDiscardCards(gameState, frontChoice.playerID)) {
            eligibleHand.push({
                zone: 'hand',
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingSmashDiscard = {
            playerID: frontChoice.playerID,
            magnitude: frontChoice.magnitude,
            // why: WP-754 / D-24581 — omit-when-absent so a Smash entry projects byte-identically.
            ...(frontChoice.reward === 'draw' ? { reward: 'draw' } : {}),
            eligibleHand,
        };
    }
    // why: WP-700 / D-24519 — project the FRONT entry of G.pendingPutHandOnDeckTop with the
    // eligible hand cards recomputed fresh via getEligiblePutHandOnDeckTopCards — the SAME
    // predicate the resolve move validates with, so the client's { cardId } selection always
    // round-trips. Each card lives in the chooser's hand (zone 'hand'); each display is spread
    // fresh (aliasing defense, WP-111 D-11105). No magnitude — the placement is mandatory, so the
    // prompt needs no reward label. Chooser-only redaction is enforced by filterUIStateForAudience
    // (keyed on .playerID), mirroring pendingSmashDiscard.
    let pendingPutHandOnDeckTop;
    if (gameState.pendingPutHandOnDeckTop !== undefined &&
        gameState.pendingPutHandOnDeckTop.length > 0) {
        const frontChoice = gameState.pendingPutHandOnDeckTop[0];
        const eligibleHand = [];
        for (const cardId of getEligiblePutHandOnDeckTopCards(gameState, frontChoice.playerID)) {
            eligibleHand.push({
                zone: 'hand',
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingPutHandOnDeckTop = {
            playerID: frontChoice.playerID,
            eligibleHand,
        };
    }
    // why: WP-681 / D-24498 — project the FRONT entry of G.pendingDoOverChoices as a binary
    // accept/decline prompt (no eligible-card list — Do-Over discards the WHOLE hand, so there
    // is nothing to pick). handSize is the current hand count so the client can label the cost
    // ("Discard N cards and draw 4"). Chooser-only redaction is enforced by
    // filterUIStateForAudience (keyed on .playerID), mirroring pendingSmashDiscard.
    let pendingDoOver;
    if (gameState.pendingDoOverChoices !== undefined &&
        gameState.pendingDoOverChoices.length > 0) {
        const frontChoice = gameState.pendingDoOverChoices[0];
        const chooserZones = gameState.playerZones[frontChoice.playerID];
        pendingDoOver = {
            playerID: frontChoice.playerID,
            handSize: chooserZones !== undefined ? chooserZones.hand.length : 0,
            drawCount: DO_OVER_DRAW_COUNT,
        };
    }
    // why: WP-498 / D-24301 — project the FRONT entry of G.pendingReturnOnDiscard with the
    // single returnable card recomputed fresh via getEligibleReturnOnDiscardCards — the SAME
    // predicate the resolve move validates with, so the client's { cardId } selection always
    // round-trips. The card lives in the chooser's discard pile (zone 'discard'). Each display
    // is spread fresh (aliasing defense, WP-111 D-11105). Chooser-only redaction is enforced by
    // filterUIStateForAudience (keyed on .playerID), mirroring pendingDiscardToPlay.
    // why: D-24527 — suppress the return-on-discard prompt while a MANDATORY
    // discard-to-play cost is still being paid. The move-level priority guard
    // (resolveReturnOnDiscard) already no-ops the return in that window, so surfacing
    // the prompt would only offer a dead-click; the projection mirrors the block-all
    // priority (discard-to-play outranks return-on-discard) so the client is shown only
    // the actionable choice. The return prompt reappears once the cost is fully paid.
    // why: WP-793 / D-24656 — project only an ACTIVE-player front entry. A non-active owner
    // answers only through its 'return-on-discard' seat choice (opened in turn.onMove); showing
    // it the legacy prompt too would let one click pop the entry and spend the seat's single
    // stage-ride move (moveLimit 1), stranding the seat choice addressed to it.
    let pendingReturnOnDiscard;
    if (gameState.pendingReturnOnDiscard !== undefined &&
        gameState.pendingReturnOnDiscard.length > 0 &&
        gameState.pendingReturnOnDiscard[0].playerID === ctx.currentPlayer &&
        !hasPendingDiscardToPlay(gameState)) {
        const frontReturn = gameState.pendingReturnOnDiscard[0];
        const eligibleReturnCards = [];
        for (const cardId of getEligibleReturnOnDiscardCards(gameState, frontReturn.playerID)) {
            eligibleReturnCards.push({
                zone: 'discard',
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingReturnOnDiscard = {
            playerID: frontReturn.playerID,
            eligibleReturnCards,
        };
    }
    // why: WP-532 / D-24343 — project the FRONT entry of G.pendingGiveHqHeroChoices with the
    // gainable HQ Heroes recomputed fresh via getEligibleGiveHqHeroCards — the SAME predicate
    // the resolve move validates with, so the client's { cardId } selection always round-trips.
    // The Heroes live in the PUBLIC G.hq; each display is spread fresh (aliasing defense, WP-111
    // D-11105). Chooser-only redaction is enforced by filterUIStateForAudience (keyed on
    // .playerID), so only the fighting player sees the prompt.
    let pendingGiveHqHeroChoice;
    if (gameState.pendingGiveHqHeroChoices !== undefined &&
        gameState.pendingGiveHqHeroChoices.length > 0) {
        const frontGive = gameState.pendingGiveHqHeroChoices[0];
        const eligibleGiveCards = [];
        for (const cardId of getEligibleGiveHqHeroCards(gameState, frontGive.playerID)) {
            eligibleGiveCards.push({
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingGiveHqHeroChoice = {
            choiceType: 'give-hq-hero',
            playerID: frontGive.playerID,
            eligible: eligibleGiveCards,
            // why: WP-692 / D-24509 — carry the optional ("may") flag so the chooser-only
            // renderer shows a Decline button for Dark Technology; absent for the mandatory
            // forms (Paibok, Bitter Captor).
            ...(frontGive.optional === true ? { optional: true } : {}),
        };
    }
    // why: WP-535 / D-24345 — project the FRONT entry of G.pendingCopyPowersChoices with the
    // copyable Heroes recomputed fresh via getEligibleCopyPowersCards — the SAME predicate the
    // resolve move validates with, so the client's { cardId } selection always round-trips.
    // The Heroes live in the current player's PUBLIC inPlay; each display is spread fresh
    // (aliasing defense, WP-111 D-11105). Chooser-only redaction is enforced by
    // filterUIStateForAudience (keyed on .playerID), so only the copying player sees the prompt.
    let pendingCopyPowersChoice;
    if (gameState.pendingCopyPowersChoices !== undefined &&
        gameState.pendingCopyPowersChoices.length > 0) {
        const frontCopy = gameState.pendingCopyPowersChoices[0];
        const eligibleCopyCards = [];
        for (const cardId of getEligibleCopyPowersCards(gameState, frontCopy.playerID)) {
            eligibleCopyCards.push({
                cardId,
                display: { ...resolveDisplay(cardId, gameState) },
            });
        }
        pendingCopyPowersChoice = {
            choiceType: 'copy-powers',
            playerID: frontCopy.playerID,
            eligible: eligibleCopyCards,
        };
    }
    // --- 13d. Project hollow-effect diagnostics (read-only) ---
    // why: WP-258 — surface the WP-257 runtime channel G.diagnostics.hollowEffects
    // onto UIState. Read-only over G (buildUIState NEVER mutates G); per-record
    // fresh objects so the projection holds no reference into G.diagnostics
    // (aliasing defense, WP-111 D-11105). When the channel is absent (older
    // snapshots / narrow test mocks never wrote it), the field is OMITTED — no
    // empty-array injection that would dirty optional-field fixtures (mirrors the
    // pendingHeroChoice / gameOver omit-when-absent posture). The records are
    // public card/mechanic data, not hidden info, so the audience filter passes
    // them through value-unchanged (D-12803).
    let hollowEffects;
    if (gameState.diagnostics !== undefined &&
        gameState.diagnostics.hollowEffects.length > 0) {
        const projectedHollowEffects = [];
        for (const record of gameState.diagnostics.hollowEffects) {
            projectedHollowEffects.push({
                cardId: record.cardId,
                cardType: record.cardType,
                timing: record.timing,
                mechanic: record.mechanic,
                reason: record.reason,
                turn: record.turn,
            });
        }
        hollowEffects = projectedHollowEffects;
    }
    // --- 13e. Project per-dispatch effect traces (read-only) ---
    // why: WP-575 / D-24384 — surface the WP-488 runtime channel G.diagnostics.traces
    // onto UIState, following the section-13d hollowEffects pattern EXACTLY. Read-only
    // over G (buildUIState NEVER mutates G); per-record fresh objects — and a fresh
    // `params` object too, since `params` is a nested map — so the projection holds no
    // reference into G.diagnostics (aliasing defense, WP-111 D-11105). The channel is
    // optional AND lazily seeded (recordHollowEffect inits G.diagnostics without
    // `traces`), so BOTH the channel and its `traces` list may be absent; either way
    // the field is OMITTED (no empty-array injection that would dirty optional-field
    // fixtures — the omit-when-absent posture). The traces are public card/mechanic
    // dispatch data, not hidden info, so the audience filter passes them through
    // value-unchanged (D-12803). Traces stay INERT (D-24294) — this is read-only.
    let effectTraces;
    if (gameState.diagnostics !== undefined &&
        gameState.diagnostics.traces !== undefined &&
        gameState.diagnostics.traces.length > 0) {
        const projectedEffectTraces = [];
        for (const trace of gameState.diagnostics.traces) {
            const projectedTrace = {
                cardId: trace.cardId,
                scope: trace.scope,
                timing: trace.timing,
                effect: trace.effect,
                handler: trace.handler,
                status: trace.status,
                fireSite: trace.fireSite,
                // why: `params` is a nested scalar map — copy it into a fresh object so
                // the projection never aliases into G.diagnostics.traces[*].params.
                params: { ...trace.params },
                turn: trace.turn,
            };
            // why: WP-706 / D-24528 — carry the count-scaled `resolution` sub-record with a
            // FRESH-object copy (and a fresh `countedInputs` array) so the projection never
            // aliases into G.diagnostics (D-11105). Conditional assignment so an absent
            // `resolution` omits the key (exactOptionalPropertyTypes). This MUST mirror the
            // filter pass-through — a build-only add is dropped at the filter whitelist (EC-206).
            if (trace.resolution !== undefined) {
                const resolutionCopy = {
                    countSource: trace.resolution.countSource,
                    resource: trace.resolution.resource,
                    magnitude: trace.resolution.magnitude,
                    count: trace.resolution.count,
                    perEach: trace.resolution.perEach,
                    computedValue: trace.resolution.computedValue,
                };
                if (trace.resolution.countedInputs !== undefined) {
                    resolutionCopy.countedInputs = [...trace.resolution.countedInputs];
                }
                projectedTrace.resolution = resolutionCopy;
            }
            projectedEffectTraces.push(projectedTrace);
        }
        effectTraces = projectedEffectTraces;
    }
    // --- 14. Project game over ---
    // why: endgame state derived from G counters via evaluateEndgame
    // (pure); scores reuse the finalScores already computed for victoryVP
    // (avoids a second pass). No ctx.gameover access needed.
    let gameOver;
    const endgameResult = evaluateEndgame(gameState);
    if (endgameResult !== null) {
        // why: wire preserved as the D-6701 extension seam — current branch is
        // unreachable (`buildParBreakdown` returns `undefined`) but the follow-up
        // WP that supplies the payload only modifies `buildParBreakdown`'s body,
        // not `buildUIState`.
        const par = buildParBreakdown(gameState, ctx);
        // why: WP-715 / D-24538 — project the per-seat Synergy Realization figures
        // (WP-708/709) from the hash-excluded `G.diagnostics.conditionalClauses` so the
        // endgame report card can show them on EVERY match, not only ranked ones (they
        // previously reached the client only inside the PAR/competitive-score breakdown).
        // A display-only copy (never a reference into G); omit-when-absent so a match with
        // no conditional clauses (or a pre-WP-708 record) carries no field. Reads only the
        // hash-excluded diagnostics channel — no hashed state, no re-pin.
        const conditionalClauses = gameState.diagnostics?.conditionalClauses;
        let synergyContributions;
        if (conditionalClauses !== undefined && Object.keys(conditionalClauses).length > 0) {
            synergyContributions = {};
            for (const [seatId, entry] of Object.entries(conditionalClauses)) {
                synergyContributions[seatId] = {
                    played: entry.played,
                    assembled: entry.assembled,
                    potentialValue: entry.potentialValue,
                    realizedValue: entry.realizedValue,
                };
            }
        }
        gameOver = {
            outcome: endgameResult.outcome,
            reason: endgameResult.reason,
            scores: finalScores,
            ...(par !== undefined ? { par } : {}),
            ...(synergyContributions !== undefined ? { synergyContributions } : {}),
            // why: WP-502 / D-24306 — surface the early-end marker to clients so the
            // endgame panel labels a player-ended match and the competitive submission
            // is skipped. Omit-when-absent (no `endedEarly: false` literal) under
            // exactOptionalPropertyTypes; only ever true for a MATCH_ENDED_EARLY result.
            ...(endgameResult.endedEarly === true ? { endedEarly: true } : {}),
        };
    }
    // --- 14b. Project the final-turn warning (WP-367 / D-24159) ---
    // why: present ONLY while the deck-exhaustion latch is active and the game has
    // not yet ended, so the client shows the "final turn" banner exactly during the
    // last turn. Once gameOver is set (including the tie), the endgame screen
    // supersedes the banner, so finalTurn is suppressed. The reason names the
    // currently-empty deck when one is still empty; if a card effect refilled the
    // deck after the latch tripped, both may read non-empty, so a generic reason
    // is used (the latch persists regardless — D-24159).
    let finalTurn;
    const isFinalTurnLatched = (gameState.counters[ENDGAME_CONDITIONS.FINAL_TURN_TRIGGERED] ?? 0) > 0;
    if (isFinalTurnLatched && gameOver === undefined) {
        let reason;
        if (gameState.villainDeck.deck.length === 0) {
            reason = 'The villain deck is empty — win or lose this turn, or the game ends in a tie.';
        }
        else if (gameState.heroDeck.length === 0) {
            reason = 'The hero deck is empty — win or lose this turn, or the game ends in a tie.';
        }
        else {
            reason = 'A deck ran out — win or lose this turn, or the game ends in a tie.';
        }
        finalTurn = {
            reason,
            heroDeckRemaining: gameState.heroDeck.length,
            villainDeckRemaining: gameState.villainDeck.deck.length,
        };
    }
    return {
        game,
        players,
        // why: WP-128 / D-12806 — `escapedPile` ships safe-skip `[]` until
        // a future WP adds `G.city.escapedPile`.
        city: { spaces: citySpaces, escapedPile },
        // why: WP-111 — slots preserved verbatim (PS-6 fallback); slotDisplay
        // added as a parallel array. Length-equals-slots invariant is
        // maintained by the unified for-of loop above.
        hq: {
            ...buildHqProjection(hqSlots, hqSlotDisplay, gameState, ctx.currentPlayer),
            // why: WP-757 / D-24587 — projected only once G.hqHaunters exists (omit-when-absent).
            ...(hqHaunters !== undefined ? { haunters: hqHaunters } : {}),
        },
        mastermind,
        scheme,
        economy,
        log,
        notableEvents,
        // why: WP-214 — spread copy of G.villainAttachedHeroes prevents aliasing;
        // per-entry spread ensures inner arrays are also new references
        villainAttachedHeroes: buildVillainAttachedHeroesProjection(gameState.villainAttachedHeroes),
        progress,
        decks,
        piles,
        koPile,
        // why: WP-664 / D-24475 — always-present public Transform side-deck
        // projection; optional in the type only for fixture back-compat (see the
        // type). Populated above ([] for a non-transform game).
        transformDeck,
        // why: WP-410 / D-24222 — always-present deduped card-image manifest the
        // arena client warms at match start. Always populated ([] for an empty
        // match); optional in the type only for fixture back-compat (see the type).
        matchCardImageUrls: buildMatchCardImageManifest(gameState.cardDisplayData),
        // why: WP-789 / D-24637 — conditional spread; omitted when no reveal event exists.
        ...(notableEventCards.length > 0 ? { notableEventCards } : {}),
        ...(gameOver !== undefined ? { gameOver } : {}),
        // why: WP-367 / D-24159 — conditional spread; the final-turn banner data is
        // omitted unless a deck has been exhausted (no `finalTurn: undefined` literal
        // under exactOptionalPropertyTypes).
        ...(finalTurn !== undefined ? { finalTurn } : {}),
        ...(pendingHeroChoice !== undefined ? { pendingHeroChoice } : {}),
        ...(pendingKoHeroChoice !== undefined ? { pendingKoHeroChoice } : {}),
        // why: WP-470 / D-24282 — conditional spread so an absent choice omits the field
        // (no `pendingScryKoChoice: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingScryKoChoice !== undefined ? { pendingScryKoChoice } : {}),
        // why: WP-603 / D-24413 — conditional spread so an absent choice omits the field
        // (no `pendingMelterKoChoice: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingMelterKoChoice !== undefined ? { pendingMelterKoChoice } : {}),
        // why: WP-702 / D-24521 — conditional spread so an absent choice omits the field
        // (no `pendingRevealTopDispose: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingRevealTopDispose !== undefined ? { pendingRevealTopDispose } : {}),
        // why: WP-695 / D-24512 — spread-in only when present (no `undefined` literal under
        // exactOptionalPropertyTypes).
        ...(pendingRuthlessDictatorChoice !== undefined ? { pendingRuthlessDictatorChoice } : {}),
        // why: WP-753 / D-24580 — spread-in only when present (no `undefined` literal under
        // exactOptionalPropertyTypes).
        ...(pendingRevealThreeAssign !== undefined ? { pendingRevealThreeAssign } : {}),
        ...(pendingElectromagneticBubbleChoice !== undefined ? { pendingElectromagneticBubbleChoice } : {}),
        // why: WP-476 / D-24284 — conditional spread so an absent choice omits the field
        // (no `pendingDiscardChoice: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingDiscardChoice !== undefined ? { pendingDiscardChoice } : {}),
        ...(pendingPutCardsOnDeckChoice !== undefined ? { pendingPutCardsOnDeckChoice } : {}),
        // why: WP-479 / D-24286 — conditional spread so an absent choice omits the field
        // (no `pendingReorderChoice: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingReorderChoice !== undefined ? { pendingReorderChoice } : {}),
        // why: WP-486 / D-24291 — conditional spread so an absent choice omits the field
        // (no `pendingDefeatChoice: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingDefeatChoice !== undefined ? { pendingDefeatChoice } : {}),
        // why: WP-693 / D-24510 — conditional spread so an absent choice omits the field
        // (no `pendingKoDiscardChoice: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingKoDiscardChoice !== undefined ? { pendingKoDiscardChoice } : {}),
        ...(pendingOptionalKoReward !== undefined ? { pendingOptionalKoReward } : {}),
        // why: WP-676 / D-24492 — conditional spread so an absent choice omits the field (no
        // `pendingSmashDiscard: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingSmashDiscard !== undefined ? { pendingSmashDiscard } : {}),
        ...(pendingPutHandOnDeckTop !== undefined ? { pendingPutHandOnDeckTop } : {}),
        // why: WP-681 / D-24498 — conditional spread so an absent choice omits the field (no
        // `pendingDoOver: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingDoOver !== undefined ? { pendingDoOver } : {}),
        // why: WP-287 — conditional spread so an absent choice omits the field (no
        // `pendingDrawOrEmpowered: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingDrawOrEmpowered !== undefined ? { pendingDrawOrEmpowered } : {}),
        // why: WP-719 / D-24541 — conditional spread so an absent choice omits the field (no
        // `pendingCoveringFireChoice: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingCoveringFireChoice !== undefined ? { pendingCoveringFireChoice } : {}),
        // why: WP-795 / D-24664 — conditional spread (omit-when-undefined under
        // exactOptionalPropertyTypes), mirroring pendingCoveringFireChoice above.
        ...(pendingMoveVillainChoice !== undefined ? { pendingMoveVillainChoice } : {}),
        // why: WP-724 / D-24546 — conditional spread (omit-when-undefined under
        // exactOptionalPropertyTypes), mirroring pendingCoveringFireChoice above.
        ...(pendingSplitFaceChoice !== undefined ? { pendingSplitFaceChoice } : {}),
        // why: WP-675 / D-24490 — conditional spread so an absent choice omits the field.
        ...(pendingCountScaledChoice !== undefined ? { pendingCountScaledChoice } : {}),
        // why: WP-678 / D-24494 — conditional spread so an absent choice omits the field.
        ...(pendingUndercoverChoice !== undefined ? { pendingUndercoverChoice } : {}),
        ...(pendingSeatChoice !== undefined ? { pendingSeatChoice } : {}),
        ...(pendingPlayVillainTop !== undefined ? { pendingPlayVillainTop } : {}),
        // why: WP-313 — conditional spread so an absent pick omits the field (no
        // `pendingVictoryPileCardPick: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingVictoryPileCardPick !== undefined ? { pendingVictoryPileCardPick } : {}),
        // why: conditional spread so an absent choice omits the field (no
        // `pendingOptionalPutBottomHQ: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingOptionalPutBottomHQ !== undefined ? { pendingOptionalPutBottomHQ } : {}),
        // why: D-24132 — conditional spread so an absent choice omits the field (no
        // `pendingPutAnyNumberBottomHQ: undefined` literal under exactOptionalPropertyTypes).
        ...(pendingPutAnyNumberBottomHQ !== undefined ? { pendingPutAnyNumberBottomHQ } : {}),
        // why: D-24139 — omit-when-absent (never an `undefined` literal) under exactOptionalPropertyTypes.
        ...(pendingReturnZeroCostDiscard !== undefined ? { pendingReturnZeroCostDiscard } : {}),
        // why: WP-383 / D-24184 — omit-when-absent (never an `undefined` literal) under exactOptionalPropertyTypes.
        ...(pendingDiscardToPlay !== undefined ? { pendingDiscardToPlay } : {}),
        ...(pendingReturnOnDiscard !== undefined ? { pendingReturnOnDiscard } : {}),
        // why: WP-532 / D-24343 — omit-when-absent (never an `undefined` literal) under exactOptionalPropertyTypes.
        ...(pendingGiveHqHeroChoice !== undefined ? { pendingGiveHqHeroChoice } : {}),
        ...(pendingCopyPowersChoice !== undefined ? { pendingCopyPowersChoice } : {}),
        // why: WP-258 — conditional spread so an absent channel omits the field
        // (no `hollowEffects: undefined` literal under exactOptionalPropertyTypes,
        // and no empty-array injection that would dirty optional-field fixtures).
        ...(hollowEffects !== undefined ? { hollowEffects } : {}),
        // why: WP-575 — same conditional spread as hollowEffects so an absent /
        // empty trace channel omits the field (no `effectTraces: undefined` literal
        // under exactOptionalPropertyTypes, no empty-array injection).
        ...(effectTraces !== undefined ? { effectTraces } : {}),
        // why: WP-802 / D-24689 §1 — omit-when-off, so a normal match's UIState has no
        // isDebugMatch key at all (no `false` literal, no fixture backfill).
        ...(gameState.isDebugMatch === true ? { isDebugMatch: true } : {}),
    };
}
/**
 * Projects The Unbreakable Enigma Code with every card visible (WP-830 / D-24763). The audience
 * filter removes the cards a viewer has not seen, so this full form never reaches a client.
 *
 * @param gameState - The game state (not mutated).
 * @returns The projection, or undefined while the code is empty or absent.
 */
function buildEnigmaCodeProjection(gameState) {
    const code = gameState.enigmaCode;
    if (code === undefined || code.length === 0) {
        return undefined;
    }
    const cards = [];
    for (let position = 0; position < code.length; position++) {
        const cardId = code[position];
        const peekedBy = [];
        for (const [playerID, positions] of Object.entries(gameState.enigmaPeeks ?? {})) {
            if (positions.includes(position)) {
                peekedBy.push(playerID);
            }
        }
        cards.push({ peekedBy, display: resolveDisplay(cardId, gameState), colors: enigmaColorsOf(gameState, cardId) });
    }
    return { isRevealed: gameState.isEnigmaCodeRevealed === true, cards };
}
//# sourceMappingURL=uiState.build.js.map